import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, ArrowLeft, QrCode, Camera } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Swal from 'sweetalert2';
import QRCode from 'react-qr-code';
import LocationMap from './components/LocationMap';
import CardListIjinKonfirmasi from './components/absen-luar/CardListIjinKonfirmasi';
import LupaAbsenList from './LupaAbsenList';
import CardListIjinPersetujuan from './components/CardListIjinPersetujuan';
import CardIjinReadonly from './components/CardIjinReadonly';
import ListIjinReadonly from './components/ListIjinReadonly';
import { isPendingIzin } from './utils/izinApproval';
import { isPendingUang } from './utils/uangApproval';
import ListUangReadonly from './components/ListUangReadonly';
import CardListUangKonfirmasi from './components/CardListUangKonfirmasi';
import { OB_TASKS } from './db/obTasks';
import ObFormModal from './components/ObFormModal';
import ObConfirmationList from './components/ObConfirmationList';
import AssetTab from './components/AssetTab';
import CardCarousel from './components/CardCarousel';
import HandoverDeliveryModal, { HANDOVER_MODES } from './components/HandoverDeliveryModal';
import { deleteHandoversFromCache, readHandovers, writeHandovers } from './utils/handoverStorage';

const readLocalHandovers = readHandovers;

// ─── LaporanCard ──────────────────────────────────────────────────────────────
const LaporanCard = React.memo(({
  item, isActive, onSelect, isTeamCard = false,
  onValid, onTidakValid, isSelected, onToggle
}) => {
  const rId = `${item.absen_id}-${item.tanggal}-${item.nama}`;
  const lokasiRaw = item.lokasi || '';
  const [latStr, lngStr] = lokasiRaw.split(', ');
  const lat = parseFloat(latStr);
  const lng = parseFloat(lngStr);
  const isValidCoord = !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;

  const [alamat, setAlamat] = useState('Memuat alamat...');
  const [isLoadingAlamat, setIsLoadingAlamat] = useState(false);

  useEffect(() => {
    if (!isValidCoord) return;
    const cacheKey = `geocode_${lat}_${lng}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) { setAlamat(cached); return; }

    setIsLoadingAlamat(true);
    setAlamat('Memuat alamat...');

    fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1&zoom=18`)
      .then(res => res.json())
      .then(data => {
        const parts = [];
        if (data.address?.road) parts.push(data.address.road);
        if (data.address?.suburb) parts.push(data.address.suburb);
        if (data.address?.city || data.address?.town) parts.push(data.address.city || data.address.town);
        const shortAddress = parts.join(', ') || (data.display_name || '').split(', ').slice(0, 3).join(', ');
        const finalAddress = shortAddress || 'Alamat tidak ditemukan';
        setAlamat(finalAddress);
        localStorage.setItem(cacheKey, finalAddress);
      })
      .catch(() => setAlamat(`Lokasi: ${lat.toFixed(6)}, ${lng.toFixed(6)}`))
      .finally(() => setIsLoadingAlamat(false));
  }, [lat, lng, isValidCoord]);

  const tipeLaporan =
    item.status?.includes('Masuk') ? 'Absen Masuk'
      : item.status ? 'Absen Pulang'
        : item.log === 'lapangan' ? 'Absen Lapangan'
          : 'Absen GPS Error';

  const namaKaryawan = (!item.status && item.nama_karyawan) ? item.nama_karyawan : null;
  const fotoUrl = item.nama ? `https://jne-cilacap.com/slip/images/images/${item.nama}` : null;

  const formatTanggal = (tanggal) => {
    if (!tanggal) return '';
    return new Intl.DateTimeFormat('id-ID', {
      weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
    }).format(new Date(tanggal));
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      onClick={() => onSelect(item)}
      style={{
        minWidth: 280, maxWidth: 320, background: '#fff', borderRadius: 20,
        overflow: 'hidden',
        boxShadow: isActive ? '0 8px 25px rgba(0, 123, 209, 0.3)' : '0 4px 15px rgba(0,0,0,0.1)',
        border: isActive ? '2px solid #0099ff' : '1px solid #e0e7ff',
        cursor: 'pointer', transition: 'all 0.3s ease',
        display: 'flex', flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div style={{
        background: isActive ? 'linear-gradient(135deg, #0099ff, #0077cc)' : '#2563eb',
        color: '#fff', padding: '12px 16px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        fontWeight: 700, fontSize: 15,
      }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span>{tipeLaporan}</span>
          {namaKaryawan && (
            <span style={{ fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.9)', marginTop: 2 }}>
              {namaKaryawan}
            </span>
          )}
        </div>
        {isTeamCard && (
          <div
            onClick={(e) => { e.stopPropagation(); onToggle(rId); }}
            style={{
              width: 24, height: 24,
              background: isSelected ? '#fff' : 'rgba(255,255,255,0.2)',
              borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.2s ease',
              border: isSelected ? 'none' : '1px solid rgba(255,255,255,0.4)',
              cursor: 'pointer', flexShrink: 0,
              boxShadow: isSelected ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
            }}
          >
            {isSelected && (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            )}
          </div>
        )}
      </div>

      {/* Foto + Map Overlay */}
      <div style={{ position: 'relative', overflow: 'hidden', background: '#f0f0f0' }}>
        {fotoUrl ? (
          <img
            src={fotoUrl}
            alt="Bukti laporan"
            loading="lazy"
            style={{ width: '100%', height: 350, objectFit: 'cover', display: 'block', opacity: 0, transition: 'opacity 0.5s ease' }}
            onLoad={(e) => (e.target.style.opacity = 1)}
            onError={(e) => { e.target.src = '/placeholder-absen.jpg'; e.target.style.objectFit = 'contain'; e.target.style.opacity = 1; }}
          />
        ) : null}
        <div style={{
          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
          display: fotoUrl ? 'none' : 'flex',
          alignItems: 'center', justifyContent: 'center',
          color: '#999', fontSize: 16, padding: 20, textAlign: 'center', background: '#f0f0f0',
        }}>
          Sudah Di Validasi Atasan / HC
        </div>
        {isValidCoord && (
          <div style={{
            position: 'absolute', bottom: 0, left: 0, width: '100%', height: 80,
            boxShadow: '0 -12px 20px rgba(0,0,0,0.4) inset', zIndex: 2, pointerEvents: 'none',
          }}>
            <LocationMap lat={lat} lng={lng} address={alamat} />
          </div>
        )}
      </div>

      {/* Alamat */}
      <div style={{ marginTop: 0 }}>
        <p style={{ fontSize: 13, margin: '4px 0', color: '#0c4a6e', fontWeight: 300, fontStyle: 'italic', textAlign: 'center' }}>
          {isLoadingAlamat ? 'Memuat alamat...' : alamat || 'Lokasi tidak tersedia'}
        </p>
      </div>

      {/* Info */}
      <div style={{ padding: '0 16px 12px', textAlign: 'center', flexGrow: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#111' }}>{formatTanggal(item.tanggal)}</div>
        <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
          Shift: {item.jadwal || '-'} | Jam: {item.jam_masuk || '-'}
        </div>
      </div>

      {/* Tombol Validasi */}
      {isTeamCard && (
        <div style={{ padding: '0 16px 16px', display: 'flex', gap: 10 }}>
          <button
            onClick={(e) => { e.stopPropagation(); onValid(item); }}
            style={{ flex: 1, padding: '12px', backgroundColor: '#10b981', color: '#fff', border: 'none', borderRadius: 12, fontWeight: 600, fontSize: 15, cursor: 'pointer' }}
          >
            ✓ Valid
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onTidakValid(item); }}
            style={{ flex: 1, padding: '12px', backgroundColor: '#ef4444', color: '#fff', border: 'none', borderRadius: 12, fontWeight: 600, fontSize: 15, cursor: 'pointer' }}
          >
            ✕ Tidak Valid
          </button>
        </div>
      )}
    </motion.div>
  );
});

// ─── Komponen Utama ───────────────────────────────────────────────────────────
const Lapor = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // ── Helpers ──
  const formatTanggal = (tanggal) => {
    if (!tanggal) return '';
    return new Intl.DateTimeFormat('id-ID', {
      weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
    }).format(new Date(tanggal));
  };

  const getTaskProgress = (taskId, totalSubTasks) => {
    try {
      const progress = JSON.parse(localStorage.getItem(`ob_progress_${taskId}`) || '{}');
      const doneCount = Object.values(progress).filter(v => v.submitted).length;
      const now = new Date();
      const hasRecentSubmit = Object.values(progress).some(v => {
        if (!v.submitted || !v.time) return false;
        return (now - new Date(v.time)) < 15000;
      });
      return {
        doneCount, totalSubTasks,
        percent: totalSubTasks > 0 ? (doneCount / totalSubTasks) * 100 : 0,
        hasRecentSubmit,
      };
    } catch {
      return { doneCount: 0, totalSubTasks, percent: 0, hasRecentSubmit: false };
    }
  };

  // ── Data Dasar ──
  const dataKaryawan = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('data_karyawan') || 'null'); } catch { return null; }
  }, []);

  const dataUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('data_user') || 'null'); } catch { return null; }
  }, []);

  // ── Peran ──
  const isUserHrd = useMemo(() => dataUser?.role?.toLowerCase() === 'hrd', [dataUser]);
  const isHumanCapital = useMemo(() => dataKaryawan?.jabatan?.toLowerCase() === 'human capital', [dataKaryawan]);
  const isGeneralAffair = useMemo(() => dataKaryawan?.jabatan?.toLowerCase() === 'general affair', [dataKaryawan]);
  const isObManager = useMemo(() => isHumanCapital || isGeneralAffair, [isHumanCapital, isGeneralAffair]);
  const isSpecialAdmin = useMemo(() => [72, 475].includes(Number(dataKaryawan?.id)), [dataKaryawan]);
  const isAtasan = useMemo(() =>
    Boolean(
      dataKaryawan && (
        !['staff', 'admin'].includes(dataKaryawan.level?.toLowerCase()) ||
        isSpecialAdmin ||
        isUserHrd ||
        isHumanCapital
      )
    ),
    [dataKaryawan, isSpecialAdmin, isUserHrd, isHumanCapital]
  );
  const canApproveUang = isSpecialAdmin;
  const canApproveIjin = useMemo(() =>
    isSpecialAdmin || isAtasan || isObManager ||
    dataUser?.role?.toLowerCase() === 'koordinator' || !!dataKaryawan,
    [isSpecialAdmin, isAtasan, isObManager, dataUser, dataKaryawan]
  );

  // ── State ──
  const absenServer = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('absen_server') || '[]'); } catch { return []; }
  }, []);

  const [absenKonfirmasi, setAbsenKonfirmasi] = useState(() => {
    try { return JSON.parse(localStorage.getItem('absen_konfirmasi') || '[]'); } catch { return []; }
  });

  const [selectedItem, setSelectedItem] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    return location.state?.activeTab || 'absen';
  });
  const [pageReady, setPageReady] = useState(false);
  const [obReportsFromServer, setObReportsFromServer] = useState([]);
  const [allTodayReports, setAllTodayReports] = useState([]);
  const [optimisticPendingReports, setOptimisticPendingReports] = useState([]);
  const [serverExtraTasks, setServerExtraTasks] = useState([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState([]);
  const [badges, setBadges] = useState({ absen: 0, lupa_absen: 0, ijin: 0, kasbon: 0, ob: 0, titipan: 0 });
  const [obLoading, setObLoading] = useState(false);
  const [obTabVisited, setObTabVisited] = useState(false);
  const [isObModalOpen, setIsObModalOpen] = useState(false);
  const [selectedObTask, setSelectedObTask] = useState(null);
  const [dataTitipan, setDataTitipan] = useState(readLocalHandovers);
  const [selectedTitipQR, setSelectedTitipQR] = useState(null);
  const [selectedTitipIds, setSelectedTitipIds] = useState([]);
  const [selectedTitipScope, setSelectedTitipScope] = useState(null);
  const [deliveryItems, setDeliveryItems] = useState([]);
  const [selectedCompletedTitipIds, setSelectedCompletedTitipIds] = useState([]);
  const [shiftTerpilih, setShiftTerpilih] = useState(null);
  const [obProgressRefresh, setObProgressRefresh] = useState(0);
  const [showAllTitipanku, setShowAllTitipanku] = useState(false);
  const [showAllSayaPegang, setShowAllSayaPegang] = useState(false);
  const [showAllUntukku, setShowAllUntukku] = useState(false);
  const [showAllSelesai, setShowAllSelesai] = useState(false);
  const HO_LIMIT = 5;

  const obPollingRef = useRef(null);
  const handoverFetchRef = useRef(null);

  // ── Jam shift dari riwayat absen hari ini ──
  const obShiftJam = useMemo(() => {
    try {
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const riwayat = JSON.parse(localStorage.getItem('riwayat_absen') || '[]');
      const entry = riwayat.find(item => item.tanggal === todayStr && item.jam_masuk);
      return entry?.jam_masuk || null;
    } catch { return null; }
  }, []);

  // ── hasAbsenToday ──
  const checkAbsenToday = () => {
    try {
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const localRiwayat = JSON.parse(localStorage.getItem('riwayat_absen') || '[]');
      const serverRiwayat = JSON.parse(localStorage.getItem('absen_server') || '[]');
      const isCheckInLocal = (item) => item.tanggal === todayStr && (item.jam_masuk || item.status?.includes('Masuk') || item.masuk || item.log === 'lapangan' || item.log === 'gps error');
      const isCheckInServer = (item) => item.tanggal === todayStr && (item.status?.includes('Masuk') || item.masuk || item.log === 'lapangan' || item.log === 'gps error');
      return localRiwayat.some(isCheckInLocal) || serverRiwayat.some(isCheckInServer);
    } catch { return false; }
  };

  const [hasAbsenToday, setHasAbsenToday] = useState(() => checkAbsenToday());

  // ── mergedTasks ──
  const mergedTasks = useMemo(() => {
    const userData = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
    const currentUserId = String(userData.id);

    return OB_TASKS.map(shift => ({
      ...shift,
      tasks: shift.tasks.map(task => {
        const staticSubTasks = task.subTasks || [];
        const itemsFromServer = serverExtraTasks.filter(et => {
          if (parseInt(et.task_id) !== parseInt(task.id)) return false;
          if (isObManager) return true;
          return !et.user_id || String(et.user_id) === currentUserId;
        });

        const finalSubTasks = [...staticSubTasks];
        itemsFromServer.forEach(serverItem => {
          if (!finalSubTasks.find(s => parseInt(s.id) === parseInt(serverItem.id))) {
            finalSubTasks.push(serverItem);
          }
        });

        try {
          const localExtras = JSON.parse(localStorage.getItem(`ob_local_extra_tasks_${task.id}`) || '[]');
          localExtras.forEach(localItem => {
            if (!finalSubTasks.find(s => String(s.id) === String(localItem.id))) {
              finalSubTasks.push(localItem);
            }
          });
        } catch { /* ignore */ }

        return { ...task, subTasks: finalSubTasks };
      })
    }));
  }, [serverExtraTasks, isObManager]);

  // ── obTotalBadgeForOBUser — dideklarasikan SETELAH mergedTasks & allTodayReports ──
  const obTotalBadgeForOBUser = useMemo(() => {
    const isObUser = dataKaryawan?.jabatan?.toLowerCase() === 'office boy';
    if (!isObUser) return 0;

    const jam = obShiftJam || shiftTerpilih?.jam_masuk || null;
    const activeShifts = mergedTasks.filter((_, idx) => {
      if (!jam) return true;
      if (jam.startsWith('07') || jam.startsWith('7:') || jam.startsWith('7.')) return idx === 0;
      if (jam.startsWith('08') || jam.startsWith('8:') || jam.startsWith('8.')) return idx === 1;
      return true;
    });

    let total = 0;
    activeShifts.forEach(shiftData => {
      shiftData.tasks.forEach(task => {
        const subtasksWithRevision = obReportsFromServer.filter(r =>
          parseInt(r.task_id) === parseInt(task.id) && r.status === 'Dikonfirmasi' && r.catatan?.trim()
        );
        const revisionCount = subtasksWithRevision.filter(r => {
          return !allTodayReports.some(report =>
            parseInt(report.task_id) === parseInt(task.id) &&
            parseInt(report.subtask_index) === parseInt(r.subtask_index) &&
            report.status !== 'Dikonfirmasi'
          );
        }).length;
        total += revisionCount;

        if (task.id === 10 || task.id === 11) {
          try {
            const progressData = JSON.parse(localStorage.getItem(`ob_progress_${task.id}`) || '{}');
            const unfinishedCount = task.subTasks.filter(st => {
              const isSubmittedLocal = progressData[st.id]?.submitted;
              const isDoneByTeam = allTodayReports.some(
                r => parseInt(r.task_id) === parseInt(task.id) && parseInt(r.subtask_index) === parseInt(st.id)
              );
              return !isSubmittedLocal && !isDoneByTeam;
            }).length;
            total += unfinishedCount;
          } catch (e) {
            console.error('Error calculating unfinished extra tasks:', e);
          }
        }
      });
    });

    return total;
  }, [dataKaryawan, obShiftJam, shiftTerpilih, mergedTasks, obReportsFromServer, allTodayReports]);

  // ── Filter laporan ──
  const filteredList = useMemo(() => {
    const filterFn = (item) =>
      item.absen_id === 18 &&
      (item.log === 'lapangan' || item.log === 'gps error') &&
      item.nama !== null;
    return {
      laporanku: absenServer.filter(filterFn),
      teamku: absenKonfirmasi.filter(filterFn),
    };
  }, [absenServer, absenKonfirmasi]);

  // ── Data Ijin/Uang ──
  const ijinPengajuanLocal = useMemo(() => { try { return JSON.parse(localStorage.getItem('ijin_pengajuan') || '[]'); } catch { return []; } }, []);
  const ijinKonfirmasiLocal = useMemo(() => { try { return JSON.parse(localStorage.getItem('ijin_konfirmasi') || '[]'); } catch { return []; } }, []);
  const uangKonfirmasiPotonganLocal = useMemo(() => { try { return JSON.parse(localStorage.getItem('uang_konfirmasiPotongan') || '[]'); } catch { return []; } }, []);
  const uangPengajuanLocal = useMemo(() => {
    try {
      const kasbon = JSON.parse(localStorage.getItem('uang_pengajuanKasbon') || '[]');
      const ijin = JSON.parse(localStorage.getItem('ijin_pengajuan') || '[]');
      const idsKeuangan = ['kasbon', 'cicilan', 'lembur', 'piket_harian', 'piket_perconnote'];
      return [...(Array.isArray(kasbon) ? kasbon : []), ...(Array.isArray(ijin) ? ijin : [])]
        .filter(item => idsKeuangan.includes(item.absen_id) || item.potongan_id);
    } catch { return []; }
  }, []);

  // ── titipan ──
  const titipanku = useMemo(() => {
    const myId = String(dataKaryawan?.id);
    return dataTitipan.filter(t => String(t.sender_id) === myId);
  }, [dataTitipan, dataKaryawan]);

  const titipanSayaPegang = useMemo(() => {
    const myId = String(dataKaryawan?.id);
    return dataTitipan.filter(t =>
      String(t.current_holder_id) === myId &&
      String(t.sender_id) !== myId &&
      t.status !== 'Selesai'
    );
  }, [dataTitipan, dataKaryawan]);

  const titipanUntukku = useMemo(() => {
    const myId = String(dataKaryawan?.id);
    return dataTitipan.filter(t =>
      (String(t.recipient_id) === myId || String(t.pending_receiver_id) === myId) &&
      String(t.current_holder_id) !== myId &&
      t.status !== 'Selesai'
    );
  }, [dataTitipan, dataKaryawan]);

  const titipanUntukkuSelesai = useMemo(() => {
    const myId = String(dataKaryawan?.id);
    return dataTitipan.filter(t =>
      String(t.recipient_id) === myId &&
      t.status === 'Selesai'
    );
  }, [dataTitipan, dataKaryawan]);

  // ── API Queue ──
  const apiQueue = useMemo(() => {
    let queue = [];
    let running = false;
    const run = async () => {
      if (running || queue.length === 0) return;
      running = true;
      const { task } = queue.shift();
      try { await task(); } catch (err) { console.error(err); }
      running = false;
      run();
    };
    return { add: (task) => { queue.push({ task }); run(); } };
  }, []);

  // ── Fetch Functions ──
  const fetchExtraTasks = async () => {
    try {
      const jamMasukParam = obShiftJam || shiftTerpilih?.jam_masuk || '';
      const jamParam = jamMasukParam ? `?jam_masuk=${jamMasukParam}` : '';
      const res = await fetch(`https://jne-cilacap.com/slip/public/api/ob/tasks${jamParam}`);
      const json = await res.json();
      if (json.status === 'success') {
        const today = new Date();
        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        const extras = [];
        json.data.forEach(task => {
          (task.items || []).forEach(item => {
            if (item.updated_at && !item.updated_at.startsWith(todayStr)) return;
            extras.push({ task_id: task.id, title: item.item_name, updated_at: item.updated_at, user_id: item.user_id, id: item.id });
          });
        });
        setServerExtraTasks(extras);
        // Simpan ke cache agar tab OB langsung muncul dari cache saat dibuka berikutnya
        try { localStorage.setItem('ob_cache_extra_tasks', JSON.stringify({ data: extras, date: todayStr })); } catch { /* ignore */ }

        if (isObManager) {
          const rawPendingCount = parseInt(localStorage.getItem('badge_ob_pending_reports') || '0');
          localStorage.setItem('badge_ob', rawPendingCount);
        }
        window.dispatchEvent(new Event('badgeUpdate'));
      }
    } catch (e) {
      console.error('[OB Debug] Error fetching extra tasks:', e);
    }
  };

  const fetchServerReports = async () => {
    try {
      const res = await fetch('https://jne-cilacap.com/slip/public/api/ob/reports');
      const json = await res.json();
      if (json.status === 'success') {
        const today = new Date();
        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        const todayReports = json.data.filter(r => r.updated_at?.startsWith(todayStr));
        setAllTodayReports(todayReports);
        // Simpan ke cache agar tab OB langsung muncul dari cache saat dibuka berikutnya
        try { localStorage.setItem('ob_cache_reports', JSON.stringify({ data: todayReports, date: todayStr })); } catch { /* ignore */ }

        if (isObManager) {
          const pendingReports = todayReports.filter(r => r.status !== 'Dikonfirmasi');
          localStorage.setItem('badge_ob_pending_reports', pendingReports.length);
          // DIHAPUS: fetchExtraTasks() di sini menyebabkan double-fetch. Tab effect sudah memanggil keduanya.
        }

        const myReports = todayReports.filter(r => parseInt(r.user_id) === parseInt(dataKaryawan?.id));
        setObReportsFromServer(myReports);

        setOptimisticPendingReports(prev => prev.filter(opt =>
          !todayReports.some(r =>
            parseInt(r.task_id) === parseInt(opt.task_id) &&
            parseInt(r.subtask_index) === parseInt(opt.subtask_index)
          )
        ));
      }
    } catch (error) {
      console.error('Gagal fetch OB reports:', error);
    }
  };

  // ── Cleanup OB Progress ──
  const cleanupObProgressStorage = () => {
    try {
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const keysToRemove = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key) continue;

        if (key.startsWith('ob_progress_')) {
          try {
            const progress = JSON.parse(localStorage.getItem(key) || '{}');
            const filtered = {};
            let hasOld = false;
            Object.entries(progress).forEach(([subId, val]) => {
              if (val?.time && !val.time.startsWith(todayStr)) { hasOld = true; }
              else { filtered[subId] = val; }
            });
            if (hasOld) {
              Object.keys(filtered).length === 0
                ? keysToRemove.push(key)
                : localStorage.setItem(key, JSON.stringify(filtered));
            }
          } catch { /* ignore */ }
        }

        if (key.startsWith('ob_local_extra_tasks_')) {
          try {
            const taskId = key.replace('ob_local_extra_tasks_', '');
            const progress = JSON.parse(localStorage.getItem(`ob_progress_${taskId}`) || '{}');
            const locals = JSON.parse(localStorage.getItem(key) || '[]');
            const cleaned = locals.filter(item => Object.keys(progress).some(k => String(k) === String(item.id)));
            cleaned.length === 0 ? keysToRemove.push(key) : localStorage.setItem(key, JSON.stringify(cleaned));
          } catch { /* ignore */ }
        }

        if (key.startsWith('ob_confirmed_indexes_') && !key.includes(todayStr)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(k => localStorage.removeItem(k));
    } catch (e) {
      console.error('[OB Cleanup] Error:', e);
    }
  };

  // ── Effects ──
  useEffect(() => {
    const t = requestAnimationFrame(() => setPageReady(true));
    return () => cancelAnimationFrame(t);
  }, []);

  // Load cache OB dari localStorage + prefetch background saat mount
  useEffect(() => {
    const isObUser = dataKaryawan?.jabatan?.toLowerCase() === 'office boy';
    const shouldFetch = isObUser || isObManager;
    if (!shouldFetch) return;

    // 1. Muat cache lokal terlebih dahulu agar tab OB bisa tampil instan tanpa menunggu network
    try {
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

      const cachedExtras = JSON.parse(localStorage.getItem('ob_cache_extra_tasks') || 'null');
      if (cachedExtras?.date === todayStr && Array.isArray(cachedExtras.data)) {
        setServerExtraTasks(cachedExtras.data);
      }

      const cachedReports = JSON.parse(localStorage.getItem('ob_cache_reports') || 'null');
      if (cachedReports?.date === todayStr && Array.isArray(cachedReports.data)) {
        setAllTodayReports(cachedReports.data);
        const myReports = cachedReports.data.filter(r => parseInt(r.user_id) === parseInt(dataKaryawan?.id));
        setObReportsFromServer(myReports);
      }
    } catch { /* ignore */ }

    // 2. Fetch terbaru di background untuk menyegarkan cache
    fetchExtraTasks();
    fetchServerReports();
  }, []); // hanya saat mount

  useEffect(() => {
    const storedShift = localStorage.getItem('shift_terpilih');
    if (storedShift) {
      try { setShiftTerpilih(JSON.parse(storedShift)); } catch { /* ignore */ }
    }
  }, []);

  useEffect(() => {
    const handler = () => setHasAbsenToday(checkAbsenToday());
    window.addEventListener('storage', handler);
    window.addEventListener('badgeUpdate', handler);
    return () => {
      window.removeEventListener('storage', handler);
      window.removeEventListener('badgeUpdate', handler);
    };
  }, []);

  const fetchHandoversFromServer = useCallback(async (force = false) => {
    if (handoverFetchRef.current) return handoverFetchRef.current;

    const request = (async () => {
      const now = Date.now();
      const lastFetch = parseInt(localStorage.getItem('data_titipan_last_fetch') || '0');

      // Jika tidak di-force dan data baru diambil kurang dari 30 detik yang lalu, gunakan cache
      if (!force && (now - lastFetch < 30000)) {
        const cached = readLocalHandovers();
        setDataTitipan(cached);
        return cached;
      }

      const res = await fetch('https://jne-cilacap.com/slip/public/api/handovers');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (json.status === 'success') {
        const serverData = Array.isArray(json.data) ? json.data : [];
        const cachedData = writeHandovers(serverData);
        setDataTitipan(cachedData);
        localStorage.setItem('data_titipan_last_fetch', String(Date.now()));
        window.dispatchEvent(new Event('handoverDataUpdated'));
        window.dispatchEvent(new Event('badgeUpdate'));
        return serverData;
      }
      return readLocalHandovers();
    })();

    handoverFetchRef.current = request;
    try {
      return await request;
    } catch (e) {
      console.warn(`API handovers tidak tersedia, menggunakan cache lokal: ${e?.message || e}`);
      return readLocalHandovers();
    } finally {
      handoverFetchRef.current = null;
    }
  }, []);

  useEffect(() => {
    const loadTitipan = () => setDataTitipan(readLocalHandovers());
    fetchHandoversFromServer();
    window.addEventListener('storage', loadTitipan);
    window.addEventListener('handoverDataUpdated', loadTitipan);
    return () => {
      window.removeEventListener('storage', loadTitipan);
      window.removeEventListener('handoverDataUpdated', loadTitipan);
    };
  }, [fetchHandoversFromServer]);

  useEffect(() => {
    if (activeTab === 'ho') {
      fetchHandoversFromServer();
    }
  }, [activeTab, fetchHandoversFromServer]);

  useEffect(() => {
    const updateBadges = () => {
      const rawAbsen = localStorage.getItem('badge_absen');
      const rawLupaAbsen = localStorage.getItem('badge_lupa_absen');
      const rawIjin = localStorage.getItem('badge_izin');
      const rawUang = localStorage.getItem('badge_uang');
      const rawOb = parseInt(localStorage.getItem('badge_ob') || '0') || 0;
      const rawTitipan = localStorage.getItem('badge_titipan');

      if (rawAbsen !== null) {
        setBadges({
          absen: parseInt(rawAbsen) || 0,
          lupa_absen: parseInt(rawLupaAbsen) || 0,
          ijin: parseInt(rawIjin) || 0,
          kasbon: parseInt(rawUang) || 0,
          ob: rawOb,
          titipan: parseInt(rawTitipan) || 0,
        });
      } else {
        try {
          const dataAbsen = JSON.parse(localStorage.getItem('absen_konfirmasi') || '[]');
          const countAbsen = dataAbsen.filter(item => item.absen_id === 18 && item.nama !== null && (item.log === 'lapangan' || item.log === 'gps error')).length;
          const dataLupaAbsen = JSON.parse(localStorage.getItem('lapor_lupa_absen') || '[]');

          // Hitung badge izin: gabungan pengajuan saya (pending) + konfirmasi tim (pending)
          const idsKeuangan = ['kasbon', 'cicilan', 'lembur', 'piket_harian', 'perconnote', 'piket_perconnote'];
          const dataIzin = JSON.parse(localStorage.getItem('ijin_konfirmasi') || '[]');
          const countIzinTeam = dataIzin.filter(item =>
            item.status === 'Izin' &&
            item.acc != 2 &&
            item.telat != 2 &&
            isPendingIzin(item)
          ).length;
          // Pengajuan izin saya sendiri yang masih pending (non-keuangan)
          const dataPengajuanIzin = JSON.parse(localStorage.getItem('ijin_pengajuan') || '[]');
          const countIzinSaya = Array.isArray(dataPengajuanIzin)
            ? dataPengajuanIzin.filter(it =>
                !idsKeuangan.includes(it.absen_id) &&
                !it.potongan_id &&
                isPendingIzin(it)
              ).length
            : 0;
          const countIzin = countIzinTeam + countIzinSaya;

          const dataUang = JSON.parse(localStorage.getItem('uang_konfirmasiPotongan') || '[]');
          const dataUangPengajuan = JSON.parse(localStorage.getItem('uang_pengajuanKasbon') || '[]');
          const dataUangDariIjin = JSON.parse(localStorage.getItem('ijin_pengajuan') || '[]');
          const countUang =
            (Array.isArray(dataUang) ? dataUang.filter(isPendingUang).length : 0) +
            (Array.isArray(dataUangPengajuan) ? dataUangPengajuan.filter(isPendingUang).length : 0) +
            (Array.isArray(dataUangDariIjin) ? dataUangDariIjin.filter(isPendingUang).length : 0);
          
          // Hitung titipan pending
          const dataTitipan = JSON.parse(localStorage.getItem('data_titipan') || '[]');
          const myId = String(dataKaryawan?.id);
          const countTitipan = dataTitipan.filter(t =>
            String(t.recipient_id) === myId &&
            String(t.current_holder_id) !== myId &&
            t.status !== 'Selesai'
          ).length;
          
          setBadges({ absen: countAbsen, lupa_absen: Array.isArray(dataLupaAbsen) ? dataLupaAbsen.length : 0, ijin: countIzin, kasbon: countUang, ob: rawOb, titipan: countTitipan });
        } catch { setBadges({ absen: 0, lupa_absen: 0, ijin: 0, kasbon: 0, ob: 0, titipan: 0 }); }
      }
    };
    updateBadges();
    window.addEventListener('storage', updateBadges);
    window.addEventListener('badgeUpdate', updateBadges);
    const interval = setInterval(updateBadges, 3000);
    return () => {
      window.removeEventListener('storage', updateBadges);
      window.removeEventListener('badgeUpdate', updateBadges);
      clearInterval(interval);
    };
  }, [dataKaryawan]);

  // Sync badge OB untuk Office Boy
  useEffect(() => {
    if (dataKaryawan?.jabatan?.toLowerCase() === 'office boy') {
      localStorage.setItem('badge_ob', obTotalBadgeForOBUser);
      window.dispatchEvent(new Event('badgeUpdate'));
    }
  }, [obTotalBadgeForOBUser, dataKaryawan]);

  // Sync selectedObTask dengan mergedTasks terbaru
  useEffect(() => {
    if (selectedObTask) {
      const updatedTask = mergedTasks.flatMap(s => s.tasks).find(t => parseInt(t.id) === parseInt(selectedObTask.id));
      if (updatedTask) setSelectedObTask(updatedTask);
    }
  }, [mergedTasks]);

  // Listen ob-tasks-updated
  useEffect(() => {
    const handle = () => { fetchExtraTasks(); fetchServerReports(); };
    window.addEventListener('ob-tasks-updated', handle);
    return () => window.removeEventListener('ob-tasks-updated', handle);
  }, []);

  // Tab OB: polling & loading
  useEffect(() => {
    if (activeTab === 'ob') {
      cleanupObProgressStorage();
      setHasAbsenToday(checkAbsenToday());

      // Jika sudah ada data (pre-fetched saat mount), langsung tampil tanpa loading
      const hasExistingData = allTodayReports.length > 0 || obReportsFromServer.length > 0 || serverExtraTasks.length > 0;

      if (!obTabVisited) {
        setObTabVisited(true);
        if (!hasExistingData) {
          // Baru pertama buka & belum ada data sama sekali: tampilkan loading
          setObLoading(true);
          Promise.all([fetchExtraTasks(), fetchServerReports()])
            .finally(() => setObLoading(false));
        } else {
          // Data sudah ada dari prefetch saat mount: langsung tampil, refresh di background
          setObLoading(false);
          fetchExtraTasks();
          fetchServerReports();
        }
      } else {
        // Sudah pernah buka tab OB sebelumnya: refresh di background tanpa loading
        fetchExtraTasks();
        fetchServerReports();
      }

      if (obPollingRef.current) clearInterval(obPollingRef.current);
      obPollingRef.current = setInterval(() => {
        fetchExtraTasks();
        if (isObManager) fetchServerReports();
      }, 60000);
    } else {
      if (obPollingRef.current) { clearInterval(obPollingRef.current); obPollingRef.current = null; }
    }
    return () => { if (obPollingRef.current) { clearInterval(obPollingRef.current); obPollingRef.current = null; } };
  }, [activeTab]);

  // ── Handlers ──
  const handleSelect = (item) => {
    setSelectedItem(item);
    localStorage.setItem('shift_terpilih', JSON.stringify(item));
  };

  const toggleSelectTeam = (id) => {
    setSelectedTeamIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAllTeam = (list) => {
    if (selectedTeamIds.length === list.length) setSelectedTeamIds([]);
    else setSelectedTeamIds(list.map(item => `${item.absen_id}-${item.tanggal}-${item.nama}`));
  };

  const toggleSelectTitip = (id, scope) => {
    if (selectedTitipScope !== scope) {
      setSelectedTitipScope(scope);
      setSelectedTitipIds([id]);
      return;
    }
    setSelectedTitipIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAllTitip = (list, scope) => {
    const selectableTitipIds = list.filter(t => {
      const isComplete = t.status === 'Selesai' || t.status === 'Diterima';
      const isTransit = t.status === 'Diserahkan';
      const canContinueManualTransit = isTransit && t.handover_mode === HANDOVER_MODES.MANUAL;
      return !isComplete && (!isTransit || canContinueManualTransit);
    }).map(t => t.id);
    
    if (selectedTitipScope === scope && selectedTitipIds.length === selectableTitipIds.length) {
      setSelectedTitipIds([]);
    } else {
      setSelectedTitipScope(scope);
      setSelectedTitipIds(selectableTitipIds);
    }
  };

  const toggleSelectCompletedTitip = (id) => {
    setSelectedCompletedTitipIds(prev =>
      prev.includes(id) ? prev.filter(itemId => itemId !== id) : [...prev, id]
    );
  };

  const toggleSelectAllCompletedTitip = (list = titipanUntukkuSelesai) => {
    const completedIds = list
      .filter(item => ['selesai', 'diterima'].includes(String(item.status).toLowerCase()))
      .map(item => item.id);
    setSelectedCompletedTitipIds(prev =>
      completedIds.every(id => prev.includes(id))
        ? prev.filter(id => !completedIds.includes(id))
        : [...new Set([...prev, ...completedIds])]
    );
  };

  const handleDeleteCompletedTitip = async () => {
    if (selectedCompletedTitipIds.length === 0) return;

    const result = await Swal.fire({
      title: 'Hapus Riwayat?',
      html: `<b>${selectedCompletedTitipIds.length} data</b> yang dipilih akan dihapus.<br><br>Data yang sudah dihapus <b>tidak dapat dikembalikan</b>.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Ya, Hapus',
      cancelButtonText: 'Batal',
      confirmButtonColor: '#ef4444',
      reverseButtons: true,
      focusCancel: true,
    });

    if (!result.isConfirmed) return;

    const updated = deleteHandoversFromCache(selectedCompletedTitipIds);
    setDataTitipan(updated);
    setSelectedCompletedTitipIds([]);
    window.dispatchEvent(new Event('handoverDataUpdated'));
    window.dispatchEvent(new Event('badgeUpdate'));

    Swal.fire({
      icon: 'success',
      title: 'Riwayat Dihapus',
      text: 'Data yang dipilih telah dihapus.',
      timer: 1600,
      showConfirmButton: false,
    });
  };

  const handleBulkDeliverTitipan = () => {
    if (selectedTitipIds.length === 0) return;
    const selected = dataTitipan.filter(item => selectedTitipIds.includes(item.id));
    const destinations = new Set(selected.map(item => `${item.recipient_id}|${item.kantor}`));
    if (destinations.size > 1) {
      Swal.fire('Tujuan Berbeda', 'Pilih barang dengan penerima akhir dan kantor tujuan yang sama.', 'warning');
      return;
    }
    setDeliveryItems(selected);
  };

  const handleDeliveryMode = async ({ mode, receiver_id, receiver_name, notes, photo }) => {
    if (!deliveryItems.length) return;
    if (mode === HANDOVER_MODES.QR) {
      await generateBulkQr();
      return;
    }

    const currentUser = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
    const actorId = String(currentUser.id || '');
    const actorName = `${currentUser.nama_depan || ''} ${currentUser.nama_belakang || ''}`.trim();
    const timestamp = new Date().toISOString();
    const normalizeName = value => String(value || '').toLowerCase().replace(/\s+/g, ' ').trim();

    const updates = deliveryItems.map(item => {
      const isFinalReceiver = mode === HANDOVER_MODES.MANUAL
        && normalizeName(receiver_name) === normalizeName(item.recipient_name);
      const status = isFinalReceiver
        ? 'Selesai'
        : mode === HANDOVER_MODES.UNATTENDED ? 'Menunggu Diterima' : 'Diserahkan';
      const eventNotes = mode === HANDOVER_MODES.UNATTENDED
        ? `Ditinggalkan untuk ${receiver_name}. ${notes}`
        : isFinalReceiver
          ? `Diterima langsung oleh penerima akhir (${receiver_name})${notes ? `. ${notes}` : ''}`
          : `Diserahkan langsung kepada perantara (${receiver_name})${notes ? `. ${notes}` : ''}`;

      return {
        ...item,
        status,
        handover_mode: mode,
        actual_receiver_id: receiver_id || null,
        actual_receiver_name: receiver_name,
        delivery_notes: notes,
        delivery_photo: photo || null,
        delivered_at: timestamp,
        pending_receiver_id: mode === HANDOVER_MODES.UNATTENDED ? String(receiver_id) : null,
        pending_receiver_name: mode === HANDOVER_MODES.UNATTENDED ? receiver_name : null,
        confirmation_status: isFinalReceiver ? 'confirmed' : 'pending',
        ...(isFinalReceiver ? {
          current_holder_id: item.recipient_id,
          current_holder_name: item.recipient_name,
          receiver_id: item.recipient_id,
          receiver_name,
          receive_time: timestamp,
        } : mode === HANDOVER_MODES.MANUAL ? {
          current_holder_id: receiver_id || '',
          current_holder_name: receiver_name,
        } : {}),
        timeline: [...(item.timeline || []), {
          status,
          actor_id: actorId,
          actor_name: actorName,
          receiver_id: receiver_id || null,
          receiver_name,
          receiver_type: isFinalReceiver ? 'final' : 'transit',
          method: mode,
          timestamp,
          notes: eventNotes,
          photo: photo || null,
        }],
      };
    });

    try {
      const response = await fetch('https://jne-cilacap.com/slip/public/api/handovers/deliver', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ids: updates.map(item => item.id),
          mode,
          receiver_id: receiver_id || null,
          receiver_name,
          notes,
          photo,
          actor_id: actorId,
          actor_name: actorName,
          timestamp,
        }),
      });
      if (!response.ok) throw new Error('Endpoint penyerahan belum tersedia');
    } catch (error) {
      console.warn('Penyerahan disimpan lokal dan menunggu sinkronisasi:', error);
    }

    const byId = new Map(updates.map(item => [String(item.id), item]));
    const cached = writeHandovers(dataTitipan.map(item => byId.get(String(item.id)) || item));
    setDataTitipan(cached);
    localStorage.setItem('data_titipan_last_fetch', '0');
    window.dispatchEvent(new Event('handoverDataUpdated'));
    window.dispatchEvent(new Event('badgeUpdate'));
    setSelectedTitipIds([]);
    setSelectedTitipScope(null);
    Swal.fire('Tercatat', mode === HANDOVER_MODES.UNATTENDED
      ? 'Bukti penempatan masuk timeline. Status belum selesai sampai penerima mengonfirmasi.'
      : 'Serah terima telah dicatat ke timeline.', 'success');
  };

  const generateBulkQr = async () => {
    if (selectedTitipIds.length === 0) return;
    
    const selectedCount = selectedTitipIds.length;
    const selectedTitipan = dataTitipan.filter(t => selectedTitipIds.includes(t.id));

    const result = await Swal.fire({
      title: 'Serahkan Barang?',
      text: `${selectedCount} barang akan diserahkan dalam 1 barcode.\nPetugas penerima akan mencatat timeline pergerakannya.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: '✓ Serahkan',
      confirmButtonColor: '#3b82f6',
    });
    
    if (!result.isConfirmed) return;

    // Generate QR code data for multiple items
    const qrData = JSON.stringify({
      ids: selectedTitipIds,
      count: selectedCount,
      timestamp: new Date().toISOString()
    });

    // Create a temporary combined object for display
    const combinedItem = {
      id: qrData,
      item_name: `${selectedCount} Barang Gabungan`,
      category: 'Multiple',
      recipient_name: selectedTitipan[0]?.recipient_name || 'N/A',
      kantor: selectedTitipan[0]?.kantor || 'N/A',
      current_holder_name: selectedTitipan[0]?.current_holder_name || 'N/A',
      sender_name: selectedTitipan[0]?.sender_name || 'N/A',
      status: 'Diproses',
      items: selectedTitipan,
      isBulk: true
    };

    // Show the combined QR code by setting it as the selected item
    // Then the existing QR display modal will show it
    setSelectedTitipIds([]);
    setSelectedTitipScope(null);
    setSelectedTitipQR(combinedItem);

    Swal.fire({
      icon: 'success',
      title: 'QR Disiapkan',
      text: `${selectedCount} barang siap diserahkan. Scan barcode untuk update status.`,
      timer: 2000,
      showConfirmButton: false
    });
  };

  const handleReceiveTitipan = async (scannedId) => {
    const currentUser = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
    const myId = String(currentUser.id);
    const myName = `${currentUser.nama_depan} ${currentUser.nama_belakang}`.trim();

    // 1. Coba kirim data scan ke API Server terlebih dahulu
    try {
      const res = await fetch('https://jne-cilacap.com/slip/public/api/handovers/receive', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scanned_text: scannedId,
          user_id: myId,
          user_name: myName
        })
      });
      const json = await res.json();
      if (json.status === 'success') {
        await fetchHandoversFromServer(true);
        Swal.fire('Berhasil', json.message || 'Barang handover berhasil diterima.', 'success');
        return;
      }
    } catch (e) {
      console.warn('API receive failed, falling back to local storage:', e);
    }

    // 2. Fallback Lokal (jika server belum siap / offline)
    let idsToReceive = [];
    try {
      const parsed = JSON.parse(scannedId);
      if (parsed && Array.isArray(parsed.ids)) {
        idsToReceive = parsed.ids;
      } else if (parsed && typeof parsed === 'object' && parsed.id) {
        idsToReceive = [parsed.id];
      } else {
        idsToReceive = [scannedId];
      }
    } catch (e) {
      idsToReceive = [scannedId];
    }

    if (idsToReceive.length === 0) return;

    const updated = dataTitipan.map(t => {
      if (idsToReceive.includes(t.id)) {
        const isFinalRecipient = String(t.recipient_id) === myId;
        const newStatus = isFinalRecipient ? 'Selesai' : 'Diserahkan';

        let baseTimeline = t.timeline || [
          {
            status: 'Dibuat',
            actor_id: t.sender_id,
            actor_name: t.sender_name,
            timestamp: t.timestamp,
            notes: `Handover dibuat oleh ${t.sender_name}`
          }
        ];

        const lastEntry = baseTimeline[baseTimeline.length - 1];
        const isDuplicateEntry = lastEntry &&
          lastEntry.status === newStatus &&
          String(lastEntry.actor_id) === String(myId);

        const timelineEntry = {
          status: newStatus,
          actor_id: myId,
          actor_name: myName,
          timestamp: new Date().toISOString(),
          notes: isFinalRecipient
            ? `Diterima oleh Penerima Akhir (${myName})`
            : `Diterima oleh Perantara (${myName})`
        };

        const newTimeline = isDuplicateEntry ? baseTimeline : [...baseTimeline, timelineEntry];

        return {
          ...t,
          status: newStatus,
          current_holder_id: myId,
          current_holder_name: myName,
          pending_receiver_id: null,
          pending_receiver_name: null,
          confirmation_status: 'confirmed',
          receive_time: new Date().toISOString(),
          receiver_id: myId,
          receiver_name: myName,
          timeline: newTimeline
        };
      }
      return t;
    });

    const cachedData = writeHandovers(updated);
    setDataTitipan(cachedData);
    localStorage.setItem('data_titipan_last_fetch', '0'); // Invalidate cache agar sinkronisasi server saat buka tab lagi
    window.dispatchEvent(new Event('handoverDataUpdated'));
    window.dispatchEvent(new Event('badgeUpdate'));
    Swal.fire('Berhasil', `${idsToReceive.length} titipan telah diterima secara offline`, 'success');
  };

  const handleBulkTeamAction = async (status) => {
    if (selectedTeamIds.length === 0) return;
    const result = await Swal.fire({
      title: status === 1 ? 'Validasi Masal?' : 'Tandai Tidak Valid?',
      text: `${selectedTeamIds.length} laporan akan diproses.`,
      icon: 'question', showCancelButton: true,
      confirmButtonText: status === 1 ? '✓ Ya, Validkan' : '✗ Tidak Valid',
      confirmButtonColor: status === 1 ? '#10b981' : '#ef4444',
    });
    if (!result.isConfirmed) return;

    const toProcess = filteredList.teamku.filter(item => selectedTeamIds.includes(`${item.absen_id}-${item.tanggal}-${item.nama}`));
    setAbsenKonfirmasi(prev => prev.filter(item => !selectedTeamIds.includes(`${item.absen_id}-${item.tanggal}-${item.nama}`)));
    const updatedLocal = JSON.parse(localStorage.getItem('absen_konfirmasi') || '[]').filter(item => !selectedTeamIds.includes(`${item.absen_id}-${item.tanggal}-${item.nama}`));
    localStorage.setItem('absen_konfirmasi', JSON.stringify(updatedLocal));
    setSelectedTeamIds([]);

    toProcess.forEach(item => {
      apiQueue.add(async () => {
        try {
          const apiPath = status === 1 ? 'valid' : 'tidak-valid';
          await fetch(`https://jne-cilacap.com/slip/public/api/absen/${apiPath}/${item.id}`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
        } catch (e) { console.error('Bulk action failed for ID:', item.id); }
      });
    });

    Swal.fire({ icon: status === 1 ? 'success' : 'info', title: 'Berhasil Diproses', text: `${toProcess.length} laporan sedang diperbarui di latar belakang.`, timer: 2000, showConfirmButton: false });
  };

  const handleValid = async (item) => {
    const result = await Swal.fire({
      title: 'Foto Benar Valid?',
      text: `Jika foto valid, uang makan akan diberikan kepada ${item.nama_karyawan}`,
      icon: 'question', showCancelButton: true,
      confirmButtonColor: '#10b981', cancelButtonColor: '#6b7280',
      confirmButtonText: '✓ Ya, Valid', cancelButtonText: 'Batal', reverseButtons: true,
    });
    if (!result.isConfirmed) return;

    setSelectedItem(null);
    setAbsenKonfirmasi(prev => prev.filter(x => !(x.tanggal === item.tanggal && x.nama === item.nama)));
    const updatedLocal = JSON.parse(localStorage.getItem('absen_konfirmasi') || '[]').filter(x => !(x.tanggal === item.tanggal && x.nama === item.nama));
    localStorage.setItem('absen_konfirmasi', JSON.stringify(updatedLocal));

    Swal.fire({ icon: 'success', title: 'Tervalidasi!', text: 'Absen dinyatakan VALID', timer: 1500, showConfirmButton: false, toast: true, position: 'top-end', background: '#10b981', color: 'white', timerProgressBar: true });

    apiQueue.add(async () => {
      try {
        const data = await (await fetch(`https://jne-cilacap.com/slip/public/api/absen/valid/${item.id}`, { method: 'POST', headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } })).json();
        if (!data.success) throw new Error(data.message || 'Gagal validasi');
      } catch {
        setAbsenKonfirmasi(prev => prev.some(x => x.tanggal === item.tanggal && x.nama === item.nama) ? prev : [...prev, item]);
        const rb = JSON.parse(localStorage.getItem('absen_konfirmasi') || '[]');
        if (!rb.some(x => x.tanggal === item.tanggal && x.nama === item.nama)) { rb.push(item); localStorage.setItem('absen_konfirmasi', JSON.stringify(rb)); }
        Swal.fire({ icon: 'warning', title: 'Gagal Sinkron', text: 'Validasi gagal disimpan ke server. Card dikembalikan.', timer: 3000, showConfirmButton: false, toast: true, position: 'top-end', background: '#f59e0b', color: 'white' });
      }
    });
  };

  const handleTidakValid = async (item) => {
    const result = await Swal.fire({
      title: 'Foto TIDAK Valid?',
      text: `Absen ${item.nama_karyawan} akan ditandai tidak valid dan jam di-reset.`,
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#ef4444', cancelButtonColor: '#6b7280',
      confirmButtonText: '✗ Tidak Valid', cancelButtonText: 'Batal', reverseButtons: true,
    });
    if (!result.isConfirmed) return;

    setSelectedItem(null);
    setAbsenKonfirmasi(prev => prev.filter(x => !(x.tanggal === item.tanggal && x.nama === item.nama)));
    const updatedLocal = JSON.parse(localStorage.getItem('absen_konfirmasi') || '[]').filter(x => !(x.tanggal === item.tanggal && x.nama === item.nama));
    localStorage.setItem('absen_konfirmasi', JSON.stringify(updatedLocal));

    Swal.fire({ icon: 'info', title: 'Ditandai Tidak Valid', timer: 1500, showConfirmButton: false, toast: true, position: 'top-end', background: '#f97316', color: 'white' });

    apiQueue.add(async () => {
      try {
        const data = await (await fetch(`https://jne-cilacap.com/slip/public/api/absen/tidak-valid/${item.id}`, { method: 'POST', headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } })).json();
        if (!data.success) throw new Error(data.message || 'Gagal');
      } catch {
        setAbsenKonfirmasi(prev => prev.some(x => x.tanggal === item.tanggal && x.nama === item.nama) ? prev : [...prev, item]);
        const rb = JSON.parse(localStorage.getItem('absen_konfirmasi') || '[]');
        if (!rb.some(x => x.tanggal === item.tanggal && x.nama === item.nama)) { rb.push(item); localStorage.setItem('absen_konfirmasi', JSON.stringify(rb)); }
        Swal.fire({ icon: 'warning', title: 'Gagal Sinkron', text: 'Gagal menyimpan ke server. Card dikembalikan.', timer: 3000, toast: true, position: 'top-end', showConfirmButton: false });
      }
    });
  };

  // ── Render Helpers ──
  const renderCardList = (list, title) => {
    const isTeam = title === 'Laporan Teamku';
    if (list.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '40px 20px', background: '#f9fafb', borderRadius: 16, border: '1px dashed #e5e7eb' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>{isTeam ? '👥' : '📝'}</div>
          <p style={{ margin: 0, fontWeight: 600, color: '#4b5563', fontSize: 15 }}>Belum ada laporan</p>
          <p style={{ margin: '4px 0 0 0', color: '#9ca3af', fontSize: 13 }}>
            {isTeam ? 'Tim kamu belum memiliki laporan absen lapangan' : 'Kamu belum memiliki laporan absen lapangan / GPS error'}
          </p>
        </div>
      );
    }
    return (
      <CardCarousel gap={20}>
        {list.map((item) => {
          const rId = `${item.absen_id}-${item.tanggal}-${item.nama}`;
          return (
            <LaporanCard
              key={rId} item={item}
              isActive={selectedItem && selectedItem.tanggal === item.tanggal && selectedItem.nama === item.nama}
              isSelected={isTeam && selectedTeamIds.includes(rId)}
              onToggle={toggleSelectTeam}
              onSelect={handleSelect}
              isTeamCard={isTeam}
              onValid={handleValid}
              onTidakValid={handleTidakValid}
            />
          );
        })}
      </CardCarousel>
    );
  };

  // ── Skeleton ──
  if (!pageReady) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'linear-gradient(180deg, #F5F9FF 0%, #E8F1FF 40%, #FFFFFF 100%)', display: 'flex', flexDirection: 'column', fontFamily: 'Poppins, sans-serif' }}>
        <div style={{ padding: '15px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(90deg, #e2e8f0 25%, #cbd5e1 50%, #e2e8f0 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite', flexShrink: 0 }} />
          <div style={{ display: 'flex', gap: 8, flex: 1 }}>
            {[90, 70, 55, 65].map((w, i) => (
              <div key={i} style={{ width: w, height: 36, borderRadius: 12, background: 'linear-gradient(90deg, #e2e8f0 25%, #cbd5e1 50%, #e2e8f0 75%)', backgroundSize: '200% 100%', animation: `shimmer 1.4s infinite ${i * 0.1}s` }} />
            ))}
          </div>
        </div>
        <div style={{ flex: 1, padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {[1, 2, 3].map(i => (
            <div key={i} style={{ backgroundColor: '#fff', borderRadius: 24, padding: '24px 20px', boxShadow: '0 4px 20px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
                <div style={{ height: 16, width: '50%', borderRadius: 8, background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' }} />
                <div style={{ height: 16, width: '20%', borderRadius: 8, background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' }} />
              </div>
              <div style={{ height: 140, borderRadius: 16, marginBottom: 16, background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' }} />
              {[75, 55].map((w, j) => (<div key={j} style={{ height: 11, width: `${w}%`, borderRadius: 8, marginBottom: 8, background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)', backgroundSize: '200% 100%', animation: `shimmer 1.4s infinite ${j * 0.1}s` }} />))}
            </div>
          ))}
        </div>
        <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
      </div>
    );
  }

  // ── Render Utama ──
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'linear-gradient(180deg, #F5F9FF 0%, #E8F1FF 40%, #FFFFFF 100%)', display: 'flex', flexDirection: 'column', fontFamily: 'Poppins, sans-serif' }}>
      {/* Header */}
      <div style={{ position: 'sticky', top: 0, zIndex: 100, background: '#F5F9FF', padding: '15px 20px', display: 'flex', alignItems: 'center', gap: 12, maxWidth: 490, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
        <motion.div onClick={() => navigate('/')} whileTap={{ scale: 0.9 }} style={{ height: 40, width: 40, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', borderRadius: '50%', cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.15)', border: 'none' }}>
          <ArrowLeft size={22} color="#333" />
        </motion.div>

        {['ho', 'ob', 'asset'].includes(activeTab) ? (
          <div style={{ flex: 1, textAlign: 'center', marginRight: 40 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#003087' }}>
              {activeTab === 'ho' ? 'Handover (HO)' : activeTab === 'ob' ? 'Tugas Office Boy' : 'Kelola Asset'}
            </h2>
          </div>
        ) : (
          <div style={{ flex: '0 1 auto', width: 'fit-content', maxWidth: 'calc(100% - 52px)', marginLeft: 'auto', overflowX: 'auto', display: 'flex', justifyContent: 'flex-end', backgroundColor: '#fff', borderRadius: 30, boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)', border: '1px solid #cbd5e1', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <style>{`div::-webkit-scrollbar { display: none; }`}</style>
            {['Absen', 'Izin', 'Uang']
              .map((tab, idx, arr) => {
                const key = tab.toLowerCase();
                const displayKey = key === 'izin' ? 'ijin' : key === 'uang' ? 'kasbon' : key;
                const active = activeTab === displayKey;
                const badgeCount = displayKey === 'absen'
                  ? (badges.absen + badges.lupa_absen)
                  : displayKey === 'ho'
                    ? badges.titipan
                    : displayKey === 'asset'
                      ? 0
                      : (badges[displayKey] || 0);

                return (
                  <button
                    key={key}
                    onClick={() => setActiveTab(displayKey)}
                    style={{
                      padding: '10px 18px', height: 42, fontSize: 17, fontWeight: 600,
                      whiteSpace: 'nowrap', color: active ? '#2563eb' : '#666',
                      backgroundColor: active ? '#eff6ff' : 'transparent',
                      border: 'none',
                      borderRight: idx < arr.length - 1 ? '1px solid #cbd5e1' : 'none',
                      borderRadius: idx === 0 ? '30px 0 0 30px' : idx === arr.length - 1 ? '0 30px 30px 0' : '0',
                      cursor: 'pointer', transition: 'all 0.3s ease',
                      display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0
                    }}
                  >
                    {tab}
                    {badgeCount > 0 && (
                      <div style={{ backgroundColor: '#ef4444', color: 'white', fontSize: 11, fontWeight: 'bold', padding: '2px 6px', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 16, height: 18, lineHeight: 1 }}>
                        {badgeCount > 99 ? '99+' : badgeCount}
                      </div>
                    )}
                  </button>
                );
              })}
          </div>
        )}
      </div>

      {/* Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', marginTop: -5, flexDirection: 'column', alignItems: 'center' }}
      >
        {/* ── Tab Absen ── */}
        {activeTab === 'absen' && (
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={{ backgroundColor: '#fff', borderRadius: 24, padding: '24px 20px', width: '100%', maxWidth: 450, margin: '0 auto 24px auto', boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}>
              <div style={{ textAlign: 'center', marginBottom: 32, paddingBottom: 24, borderBottom: '1px solid #f0f0f0' }}>
                <h2 style={{ fontWeight: 700, fontSize: 18, margin: '0 0 2px 0', color: '#111827' }}>Laporanku</h2>
                <p style={{ margin: 0, color: '#9ca3af', fontSize: 13 }}>{filteredList.laporanku.length} laporan diproses</p>
              </div>
              {renderCardList(filteredList.laporanku, 'Laporanku')}
            </motion.div>

            {(isAtasan || filteredList.teamku.length > 0) && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} style={{ backgroundColor: '#fff', borderRadius: 24, padding: '24px 20px', width: '100%', maxWidth: 450, margin: '0 auto 24px auto', boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}>
                <div style={{ textAlign: 'center', marginBottom: 32, paddingBottom: 24, borderBottom: '1px solid #f0f0f0' }}>
                  <h2 style={{ fontWeight: 700, fontSize: 18, margin: '0 0 2px 0', color: '#111827' }}>Laporan Teamku</h2>
                  <p style={{ margin: 0, color: '#9ca3af', fontSize: 13 }}>{filteredList.teamku.length} laporan tim menunggu persetujuan</p>
                </div>

                {/* Bulk Action */}
                <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 20, padding: '16px', backgroundColor: '#f8fafc', borderRadius: 16, border: '1px solid #e2e8f0', gap: 16 }}>
                  <div onClick={() => toggleSelectAllTeam(filteredList.teamku)} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none', alignSelf: 'flex-start' }}>
                    <div style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${(filteredList.teamku.length > 0 && selectedTeamIds.length === filteredList.teamku.length) ? '#2563eb' : '#cbd5e1'}`, backgroundColor: (filteredList.teamku.length > 0 && selectedTeamIds.length === filteredList.teamku.length) ? '#2563eb' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}>
                      {(filteredList.teamku.length > 0 && selectedTeamIds.length === filteredList.teamku.length) && (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>)}
                    </div>
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>Pilih Semua <span style={{ color: '#94a3b8', fontWeight: 500 }}>({selectedTeamIds.length})</span></span>
                  </div>
                  {selectedTeamIds.length > 0 && (
                    <div style={{ display: 'flex', gap: 12, width: '100%' }}>
                      <button onClick={() => handleBulkTeamAction(0)} style={{ flex: 1, padding: '10px 16px', backgroundColor: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                        <span style={{ fontSize: 14 }}>✕</span> Tolak
                      </button>
                      <button onClick={() => handleBulkTeamAction(1)} style={{ flex: 1, padding: '10px 16px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)' }}>
                        <span style={{ fontSize: 14 }}>✓</span> Terimakan
                      </button>
                    </div>
                  )}
                </div>
                {renderCardList(filteredList.teamku, 'Laporan Teamku')}
              </motion.div>
            )}

            {/* Sementara di-hide sesuai permintaan user */}
            {/* {isUserHrd && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} style={{ backgroundColor: '#fff', borderRadius: 24, padding: '24px 20px', width: '100%', maxWidth: 450, margin: '0 auto 24px auto', boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}>
                <LupaAbsenList />
              </motion.div>
            )} */}
          </div>
        )}

        {/* ── Tab Ijin ── */}
        {activeTab === 'ijin' && (
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <ListIjinReadonly type="ijin" />
            {canApproveIjin && <CardListIjinPersetujuan />}
          </div>
        )}

        {/* ── Tab Kasbon ── */}
        {activeTab === 'kasbon' && (
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <ListUangReadonly />
            {canApproveUang && <CardListUangKonfirmasi />}
          </div>
        )}

        {/* ── Tab OB ── */}
        {activeTab === 'ob' && (
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 24 }}>
            {obLoading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%' }}>
                {[1, 2, 3].map(i => (
                  <div key={i} style={{ backgroundColor: '#fff', borderRadius: 24, padding: '24px 20px', boxShadow: '0 4px 20px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
                      <div style={{ width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite', flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ height: 14, width: '60%', borderRadius: 8, marginBottom: 8, background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' }} />
                        <div style={{ height: 10, width: '40%', borderRadius: 8, background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' }} />
                      </div>
                    </div>
                    {[80, 65, 90].map((w, j) => (<div key={j} style={{ height: 12, width: `${w}%`, borderRadius: 8, marginBottom: 10, background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)', backgroundSize: '200% 100%', animation: `shimmer 1.4s infinite ${j * 0.1}s` }} />))}
                    <div style={{ height: 44, borderRadius: 14, marginTop: 16, background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' }} />
                  </div>
                ))}
                <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
              </div>
            ) : isObManager ? (
              <ObConfirmationList />
            ) : !hasAbsenToday ? (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} style={{ backgroundColor: '#fff', borderRadius: 24, padding: '40px 24px', textAlign: 'center', boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)', border: '1px solid #cbd5e1', margin: '0 auto 24px auto', width: '100%', maxWidth: 450, boxSizing: 'border-box' }}>
                <div style={{ fontSize: 64, marginBottom: 20 }}>⚠️</div>
                <h3 style={{ fontWeight: 800, fontSize: 20, color: '#1e293b', margin: '0 0 12px 0' }}>Belum Ada Absensi</h3>
                <p style={{ color: '#64748b', fontSize: 14, margin: '0 0 24px 0', lineHeight: 1.5 }}>Silahkan melakukan <b>Absen Masuk</b> terlebih dahulu untuk melihat dan melaporkan tugas OB hari ini.</p>
                <button onClick={() => navigate('/absen')} style={{ padding: '14px 28px', backgroundColor: '#2563eb', color: '#fff', border: 'none', borderRadius: 16, fontWeight: 700, fontSize: 15, cursor: 'pointer', boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)' }}>Pergi ke Menu Absen</button>
              </motion.div>
            ) : (
              mergedTasks.filter((_, idx) => {
                const jam = obShiftJam || shiftTerpilih?.jam_masuk || null;
                if (!jam) return true;
                if (jam.startsWith('07') || jam.startsWith('7:') || jam.startsWith('7.')) return idx === 0;
                if (jam.startsWith('08') || jam.startsWith('8:') || jam.startsWith('8.')) return idx === 1;
                return true;
              }).map((shiftData, shiftIdx) => (
                <motion.div key={shiftIdx} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: shiftIdx * 0.1 }} style={{ backgroundColor: '#fff', borderRadius: 24, padding: '24px 20px', width: '100%', maxWidth: 450, margin: '0 auto 24px auto', boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}>
                  {(() => {
                    // Hitung stats untuk header
                    const diserahkanCount = shiftData.tasks.reduce((acc, task) => {
                      const pending = obReportsFromServer.filter(r => parseInt(r.task_id) === parseInt(task.id) && r.status !== 'Dikonfirmasi').length;
                      return acc + pending;
                    }, 0);

                    const menungguCount = shiftData.tasks.reduce((acc, task) => {
                      const confirmed = obReportsFromServer.filter(r => parseInt(r.task_id) === parseInt(task.id) && r.status === 'Dikonfirmasi' && (!r.catatan || r.catatan.trim() === '')).length;
                      return acc + confirmed;
                    }, 0);

                    const tindakanCount = shiftData.tasks.reduce((acc, task) => {
                      const action = obReportsFromServer.filter(r => parseInt(r.task_id) === parseInt(task.id) && r.status === 'Dikonfirmasi' && r.catatan?.trim()).length;
                      return acc + action;
                    }, 0);

                    const totalPercent = diserahkanCount + menungguCount === 0 ? 0 : Math.round(((menungguCount + tindakanCount) / (diserahkanCount + menungguCount + tindakanCount)) * 100);

                    return (
                      <div style={{ backgroundColor: '#2563eb', borderRadius: 20, padding: '20px', marginBottom: 20, color: '#fff' }}>
                        {/* Header: Shift name + Percent */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 600, opacity: 0.9, marginBottom: 2 }}>Shift Awal</div>
                            <div style={{ fontSize: 20, fontWeight: 800 }}>
                              {shiftData.shift.split(' – ')[0]} - {shiftData.shift.split(' – ')[1]}
                            </div>
                          </div>
                          <div style={{
                            backgroundColor: '#22c55e',
                            color: '#166534',
                            padding: '8px 16px',
                            borderRadius: 12,
                            fontWeight: 800,
                            fontSize: 18
                          }}>
                            {totalPercent}%
                          </div>
                        </div>

                        {/* Stat boxes */}
                        <div style={{ display: 'flex', gap: 12 }}>
                          {/* Diserahkan */}
                          <div style={{
                            flex: 1,
                            backgroundColor: 'rgba(255,255,255,0.15)',
                            borderRadius: 12,
                            padding: '12px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center'
                          }}>
                            <div style={{ fontSize: 24, marginBottom: 4 }}>✓</div>
                            <div style={{ fontSize: 11, opacity: 0.85, marginBottom: 4 }}>Diserahkan</div>
                            <div style={{
                              fontSize: 20,
                              fontWeight: 800,
                              position: 'relative'
                            }}>
                              {diserahkanCount}
                            </div>
                          </div>

                          {/* Menunggu */}
                          <div style={{
                            flex: 1,
                            backgroundColor: 'rgba(255,255,255,0.15)',
                            borderRadius: 12,
                            padding: '12px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center'
                          }}>
                            <div style={{ fontSize: 24, marginBottom: 4 }}>🕒</div>
                            <div style={{ fontSize: 11, opacity: 0.85, marginBottom: 4 }}>Menunggu</div>
                            <div style={{
                              fontSize: 20,
                              fontWeight: 800
                            }}>
                              {menungguCount}
                            </div>
                          </div>

                          {/* Tindakan */}
                          <div style={{
                            flex: 1,
                            backgroundColor: 'rgba(255,255,255,0.15)',
                            borderRadius: 12,
                            padding: '12px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center'
                          }}>
                            <div style={{ fontSize: 24, marginBottom: 4 }}>⚠️</div>
                            <div style={{ fontSize: 11, opacity: 0.85, marginBottom: 4 }}>Tindakan</div>
                            <div style={{
                              fontSize: 20,
                              fontWeight: 800
                            }}>
                              {tindakanCount}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {shiftData.tasks.map((task) => {
                      const progress = getTaskProgress(task.id, task.subTasks.length);
                      const isFinished = progress.percent === 100 && task.subTasks.length > 0;
                      const hasPendingReports = obReportsFromServer.some(r => parseInt(r.task_id) === parseInt(task.id) && r.status !== 'Dikonfirmasi') || optimisticPendingReports.some(opt => parseInt(opt.task_id) === parseInt(task.id));
                      const confirmedCount = obReportsFromServer.filter(r => parseInt(r.task_id) === parseInt(task.id) && r.status === 'Dikonfirmasi').length;
                      const isAllConfirmedByAdmin = isFinished && confirmedCount >= task.subTasks.length;

                      const subtasksWithRevision = obReportsFromServer.filter(r => parseInt(r.task_id) === parseInt(task.id) && r.status === 'Dikonfirmasi' && r.catatan?.trim());
                      const revisionCount = subtasksWithRevision.filter(r =>
                        !allTodayReports.some(report => parseInt(report.task_id) === parseInt(task.id) && parseInt(report.subtask_index) === parseInt(r.subtask_index) && report.status !== 'Dikonfirmasi')
                      ).length;
                      const hasRevision = revisionCount > 0;
                      const isConfirmed = isAllConfirmedByAdmin && !hasRevision && !hasPendingReports && !progress.hasRecentSubmit && allTodayReports.length > 0;

                      let accentColor = '#f59e0b', bgColor = '#fef3c7', statusText = `${task.subTasks.length} Sub-tugas`;
                      if (hasRevision) { accentColor = '#ef4444'; bgColor = '#fef2f2'; statusText = `⚠️ ${revisionCount} Perlu Revisi`; }
                      else if (isConfirmed) { accentColor = '#3b82f6'; bgColor = '#eff6ff'; statusText = 'Selesai & Dikonfirmasi'; }
                      else if (isFinished) { accentColor = '#10b981'; bgColor = '#dcfce7'; statusText = 'Menunggu Konfirmasi'; }
                      else if (progress.doneCount > 0) { accentColor = '#10b981'; bgColor = '#dcfce7'; statusText = `${progress.doneCount}/${task.subTasks.length} Dikerjakan`; }

                      const isObUser = dataKaryawan?.jabatan?.toLowerCase() === 'office boy';
                      let cardBadgeCount = isObUser ? revisionCount : 0;
                      if (isObUser && (task.id === 10 || task.id === 11)) {
                        try {
                          const progressData = JSON.parse(localStorage.getItem(`ob_progress_${task.id}`) || '{}');
                          cardBadgeCount += task.subTasks.filter(st => !progressData[st.id]?.submitted && !allTodayReports.some(r => parseInt(r.task_id) === parseInt(task.id) && parseInt(r.subtask_index) === parseInt(st.id))).length;
                        } catch { /* ignore */ }
                      }

                      return (
                        <div key={task.id} onClick={() => { setSelectedObTask(task); setIsObModalOpen(true); }}
                          style={{ padding: '16px', borderRadius: 20, display: 'flex', flexDirection: 'column', gap: 12, cursor: 'pointer', background: '#ffffff', border: `1px solid ${accentColor}`, boxShadow: '0 4px 12px rgba(0,0,0,0.03)', transition: 'all .2s ease', position: 'relative', overflow: 'hidden' }}
                          onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.98)')}
                          onMouseUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                        >
                          <div style={{ position: 'absolute', bottom: 0, left: 0, height: 4, backgroundColor: '#f1f5f9', width: '100%', zIndex: 1 }} />
                          <motion.div initial={{ width: 0 }} animate={{ width: isConfirmed ? '100%' : `${progress.percent}%` }} style={{ position: 'absolute', bottom: 0, left: 0, height: 4, backgroundColor: accentColor, zIndex: 2 }} />

                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              <div style={{ position: 'relative' }}>
                                <div style={{ width: 42, height: 42, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: bgColor, fontSize: 20 }}>
                                  {isConfirmed ? '✅' : progress.doneCount > 0 ? '🕒' : task.id === 1 ? '🧹' : task.id === 2 ? '🏢' : '🌳'}
                                </div>
                                {isObUser && cardBadgeCount > 0 && (
                                  <div style={{ position: 'absolute', top: -6, right: -6, backgroundColor: '#ef4444', color: '#fff', borderRadius: 999, fontSize: 10, fontWeight: 800, padding: '2px 5px', minWidth: 16, textAlign: 'center', border: '2px solid #fff', lineHeight: 1.4, boxShadow: '0 2px 6px rgba(239,68,68,0.5)' }}>
                                    {cardBadgeCount}
                                  </div>
                                )}
                              </div>
                              <div>
                                <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', lineHeight: 1.2 }}>
                                  {task.title.length > 30 ? task.title.substring(0, 30) + '...' : task.title}
                                </div>
                                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{statusText}</div>
                              </div>
                            </div>
                            <div style={{ padding: '4px 8px', borderRadius: 8, backgroundColor: bgColor, color: accentColor, fontSize: 11, fontWeight: 700, border: `1px solid ${accentColor}` }}>
                              {isConfirmed ? 'DONE' : `${progress.doneCount}/${progress.totalSubTasks}`}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              ))
            )}
          </div>
        )}

        {/* ── Tab Titip ── */}
        {activeTab === 'ho' && (
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 24 }}>
            {/* Shortcut Form */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} onClick={() => navigate('/handover', { state: { jenis: 'titip' } })}
              style={{ backgroundColor: '#fff', borderRadius: 24, padding: '20px', width: '100%', maxWidth: 450, margin: '0 auto 24px auto', boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)', border: '1px solid #cbd5e1', display: 'flex', alignItems: 'center', gap: 16, cursor: 'pointer', boxSizing: 'border-box' }}
            >
              <div style={{ width: 48, height: 48, borderRadius: 14, backgroundColor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0 }}>📦</div>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#1e293b' }}>Form Serah Terima</h3>
                <p style={{ margin: '2px 0 0 0', fontSize: 12, color: '#64748b' }}>Serahkan barang/dokumen ke penerima atau perantara</p>
              </div>
              <div style={{ color: '#94a3b8' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
              </div>
            </motion.div>

            {/* Riwayat Titipku */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={{ backgroundColor: '#fff', borderRadius: 24, padding: '24px 20px', width: '100%', maxWidth: 450, margin: '0 auto 24px auto', boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}>
              <div style={{ textAlign: 'center', marginBottom: 32, paddingBottom: 24, borderBottom: '1px solid #f0f0f0' }}>
                <h2 style={{ fontWeight: 700, fontSize: 18, margin: '0 0 2px 0', color: '#111827' }}>Riwayat Serah Terima Keluar</h2>
                <p style={{ margin: 0, color: '#9ca3af', fontSize: 13 }}>{titipanku.length} barang diserahkan</p>
              </div>
              {titipanku.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {/* Multi-select toolbar */}
                  {titipanku.some(t => {
                    const isComplete = t.status === 'Selesai' || t.status === 'Diterima';
                    const isTransit = t.status === 'Diserahkan' || t.status === 'Menunggu Diterima';
                    const canContinueManualTransit = t.status === 'Diserahkan' && t.handover_mode === HANDOVER_MODES.MANUAL;
                    return !isComplete && (!isTransit || canContinueManualTransit);
                  }) && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px', backgroundColor: '#f0f9ff', borderRadius: 12, border: '1px solid #bfdbfe', marginBottom: 12 }}>
                      <div onClick={() => toggleSelectAllTitip(titipanku, 'outgoing')} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', flex: 1 }}>
                        <input
                          type="checkbox"
                          checked={selectedTitipScope === 'outgoing' && selectedTitipIds.length > 0 && titipanku.filter(t => {
                            const isComplete = t.status === 'Selesai' || t.status === 'Diterima';
                            const isTransit = t.status === 'Diserahkan';
                            const canContinueManualTransit = isTransit && t.handover_mode === HANDOVER_MODES.MANUAL;
                            return !isComplete && (!isTransit || canContinueManualTransit);
                          }).length === selectedTitipIds.length}
                          onChange={() => toggleSelectAllTitip(titipanku, 'outgoing')}
                          style={{ cursor: 'pointer', width: 18, height: 18 }}
                        />
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
                          {selectedTitipScope !== 'outgoing' || selectedTitipIds.length === 0 ? 'Pilih Semua' : `${selectedTitipIds.length} Terpilih`}
                        </span>
                      </div>
                      {selectedTitipScope === 'outgoing' && selectedTitipIds.length > 0 && (
                        <button
                          onClick={handleBulkDeliverTitipan}
                          style={{
                            padding: '8px 16px',
                            borderRadius: 8,
                            border: 'none',
                            backgroundColor: '#3b82f6',
                            color: '#fff',
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                          }}
                          onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.95)')}
                          onMouseUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                        >
                          📤 Serahkan ({selectedTitipIds.length})
                        </button>
                      )}
                    </div>
                  )}

                  {titipanku.some(t => ['selesai', 'diterima'].includes(String(t.status).toLowerCase())) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px', backgroundColor: '#fef2f2', borderRadius: 12, border: '1px solid #fecaca', marginBottom: 12 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, fontSize: 13, fontWeight: 600, color: '#475569', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={titipanku
                            .filter(t => ['selesai', 'diterima'].includes(String(t.status).toLowerCase()))
                            .every(t => selectedCompletedTitipIds.includes(t.id))}
                          onChange={() => toggleSelectAllCompletedTitip(titipanku)}
                          style={{ cursor: 'pointer', width: 18, height: 18 }}
                        />
                        {selectedCompletedTitipIds.length > 0
                          ? `${selectedCompletedTitipIds.length} Riwayat Terpilih`
                          : 'Pilih Riwayat Selesai'}
                      </label>
                      {selectedCompletedTitipIds.length > 0 && (
                        <button
                          onClick={handleDeleteCompletedTitip}
                          style={{ border: 'none', borderRadius: 8, padding: '8px 14px', backgroundColor: '#ef4444', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                        >
                          Hapus ({selectedCompletedTitipIds.length})
                        </button>
                      )}
                    </div>
                  )}

                  {(showAllTitipanku ? titipanku : titipanku.slice(0, HO_LIMIT)).map(t => {
                    const isComplete = t.status === 'Selesai' || t.status === 'Diterima';
                    const isWaiting = t.status === 'Menunggu Diterima';
                    const isTransit = t.status === 'Diserahkan' || isWaiting;
                    const isSelectable = !isComplete && (!isTransit || (t.status === 'Diserahkan' && t.handover_mode === HANDOVER_MODES.MANUAL));
                    const isSelected = selectedTitipScope === 'outgoing' && selectedTitipIds.includes(t.id);
                    const isCompletedSelected = selectedCompletedTitipIds.includes(t.id);

                    let badgeBg = '#fef9c3', badgeColor = '#854d0e';
                    if (isComplete) { badgeBg = '#dcfce7'; badgeColor = '#166534'; }
                    else if (isTransit) { badgeBg = '#eff6ff'; badgeColor = '#1e40af'; }

                    return (
                      <div 
                        key={t.id} 
                        onClick={() => setSelectedTitipQR(t)}
                        style={{
                          padding: '16px',
                          borderRadius: 18,
                          backgroundColor: isCompletedSelected ? '#fef2f2' : isSelected ? '#dbeafe' : '#f8fafc',
                          border: isCompletedSelected ? '2px solid #ef4444' : isSelected ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {isSelectable && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelectTitip(t.id, 'outgoing')}
                            onClick={(e) => e.stopPropagation()}
                            style={{ cursor: 'pointer', width: 18, height: 18 }}
                          />
                        )}
                        {isComplete && (
                          <input
                            type="checkbox"
                            checked={isCompletedSelected}
                            onChange={() => toggleSelectCompletedTitip(t.id)}
                            onClick={(e) => e.stopPropagation()}
                            style={{ cursor: 'pointer', width: 18, height: 18 }}
                            aria-label={`Pilih riwayat ${t.item_name}`}
                          />
                        )}
                        <div style={{ fontSize: 24 }}>{t.category === 'Barang' ? '📦' : '📄'}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>{t.item_name}</div>
                          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                            {isComplete
                              ? `Diterima oleh: ${t.receiver_name || t.recipient_name}`
                              : isWaiting
                                ? `Menunggu diterima oleh: ${t.pending_receiver_name || t.recipient_name}`
                                : isTransit
                                ? `Transit di: ${t.current_holder_name}`
                                : `Penerima Akhir: ${t.recipient_name} (${t.kantor})`}
                          </div>
                        </div>
                        <div style={{ fontSize: 11, fontWeight: 700, padding: '4px 8px', borderRadius: 8, backgroundColor: badgeBg, color: badgeColor }}>
                          {isComplete ? 'Selesai' : isWaiting ? 'Menunggu Diterima' : isTransit ? 'Transit' : 'Diproses'}
                        </div>
                      </div>
                    );
                  })}
                  {!showAllTitipanku && titipanku.length > HO_LIMIT && (
                    <button
                      onClick={() => setShowAllTitipanku(true)}
                      style={{ marginTop: 4, width: '100%', padding: '10px', borderRadius: 12, border: '1px dashed #cbd5e1', backgroundColor: '#f8fafc', color: '#2563eb', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s ease' }}
                    >
                      👁 Lihat Semua ({titipanku.length - HO_LIMIT} item lainnya)
                    </button>
                  )}
                  {showAllTitipanku && titipanku.length > HO_LIMIT && (
                    <button
                      onClick={() => setShowAllTitipanku(false)}
                      style={{ marginTop: 4, width: '100%', padding: '10px', borderRadius: 12, border: '1px dashed #cbd5e1', backgroundColor: '#f8fafc', color: '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s ease' }}
                    >
                      ↑ Sembunyikan
                    </button>
                  )}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8', backgroundColor: '#f8fafc', borderRadius: 20, border: '1px dashed #e2e8f0' }}>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>📁</div>
                  <p style={{ fontSize: 13, margin: 0 }}>Belum ada riwayat serah terima keluar</p>
                </div>
              )}
            </motion.div>

            {/* Titipan di Tanganku (Transit) */}
            {titipanSayaPegang.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={{ backgroundColor: '#fff', borderRadius: 24, padding: '24px 20px', width: '100%', maxWidth: 450, margin: '0 auto 24px auto', boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}>
                <div style={{ textAlign: 'center', marginBottom: 32, paddingBottom: 24, borderBottom: '1px solid #f0f0f0' }}>
                  <h2 style={{ fontWeight: 700, fontSize: 18, margin: '0 0 2px 0', color: '#111827' }}>Titipan di Tanganku</h2>
                  <p style={{ margin: 0, color: '#9ca3af', fontSize: 13 }}>{titipanSayaPegang.length} item sedang Anda bawa</p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {/* Multi-select toolbar */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px', backgroundColor: '#f0f9ff', borderRadius: 12, border: '1px solid #bfdbfe', marginBottom: 12 }}>
                    <div onClick={() => toggleSelectAllTitip(titipanSayaPegang, 'held')} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', flex: 1 }}>
                      <input
                        type="checkbox"
                        checked={selectedTitipScope === 'held' && selectedTitipIds.length > 0 && titipanSayaPegang.length === selectedTitipIds.length}
                        onChange={() => toggleSelectAllTitip(titipanSayaPegang, 'held')}
                        style={{ cursor: 'pointer', width: 18, height: 18 }}
                      />
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
                        {selectedTitipScope !== 'held' || selectedTitipIds.length === 0 ? 'Pilih Semua' : `${selectedTitipIds.length} Terpilih`}
                      </span>
                    </div>
                    {selectedTitipScope === 'held' && selectedTitipIds.length > 0 && (
                      <button
                        onClick={handleBulkDeliverTitipan}
                        style={{
                          padding: '8px 16px',
                          borderRadius: 8,
                          border: 'none',
                          backgroundColor: '#3b82f6',
                          color: '#fff',
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                        }}
                        onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.95)')}
                        onMouseUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                      >
                        📤 Serahkan ({selectedTitipIds.length})
                      </button>
                    )}
                  </div>

                  {(showAllSayaPegang ? titipanSayaPegang : titipanSayaPegang.slice(0, HO_LIMIT)).map(t => {
                    const isSelected = selectedTitipScope === 'held' && selectedTitipIds.includes(t.id);
                    return (
                      <div 
                        key={t.id} 
                        onClick={() => setSelectedTitipQR(t)}
                        style={{ 
                          padding: '16px', 
                          borderRadius: 18, 
                          backgroundColor: isSelected ? '#dbeafe' : '#eff6ff', 
                          border: isSelected ? '2px solid #3b82f6' : '1px solid #bfdbfe', 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 12, 
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelectTitip(t.id, 'held')}
                          onClick={(e) => e.stopPropagation()}
                          style={{ cursor: 'pointer', width: 18, height: 18 }}
                        />
                        <div style={{ fontSize: 24 }}>{t.category === 'Barang' ? '📦' : '📄'}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>{t.item_name}</div>
                          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                            Tujuan Akhir: <b>{t.recipient_name}</b> ({t.kantor})
                          </div>
                        </div>
                        <div style={{ fontSize: 11, fontWeight: 700, padding: '4px 8px', borderRadius: 8, backgroundColor: '#dbeafe', color: '#1e40af' }}>
                          Saya Bawa
                        </div>
                      </div>
                    );
                  })}
                  {!showAllSayaPegang && titipanSayaPegang.length > HO_LIMIT && (
                    <button
                      onClick={() => setShowAllSayaPegang(true)}
                      style={{ marginTop: 4, width: '100%', padding: '10px', borderRadius: 12, border: '1px dashed #cbd5e1', backgroundColor: '#f8fafc', color: '#2563eb', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s ease' }}
                    >
                      👁 Lihat Semua ({titipanSayaPegang.length - HO_LIMIT} item lainnya)
                    </button>
                  )}
                  {showAllSayaPegang && titipanSayaPegang.length > HO_LIMIT && (
                    <button
                      onClick={() => setShowAllSayaPegang(false)}
                      style={{ marginTop: 4, width: '100%', padding: '10px', borderRadius: 12, border: '1px dashed #cbd5e1', backgroundColor: '#f8fafc', color: '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s ease' }}
                    >
                      ↑ Sembunyikan
                    </button>
                  )}
                </div>
              </motion.div>
            )}

            {/* Titipan Untukku */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} style={{ backgroundColor: '#fff', borderRadius: 24, padding: '24px 20px', width: '100%', maxWidth: 450, margin: '0 auto 24px auto', boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}>
              <div style={{ textAlign: 'center', marginBottom: 32, paddingBottom: 24, borderBottom: '1px solid #f0f0f0' }}>
                <h2 style={{ fontWeight: 700, fontSize: 18, margin: '0 0 2px 0', color: '#111827' }}>Serah Terima Masuk (Pending)</h2>
                <p style={{ margin: 0, color: '#9ca3af', fontSize: 13 }}>{titipanUntukku.length} barang menunggu Anda terima</p>
                <button onClick={() => navigate('/lapor/scan')} style={{ marginTop: 16, padding: '10px 20px', borderRadius: 12, border: 'none', backgroundColor: '#2563eb', color: '#fff', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, margin: '16px auto 0', cursor: 'pointer' }}>
                  <QrCode size={18} /> Scan Terima Barang
                </button>
              </div>


              {titipanUntukku.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px', backgroundColor: '#f0f9ff', borderRadius: 12, border: '1px solid #bfdbfe' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', flex: 1 }}>
                      <input
                        type="checkbox"
                        checked={selectedTitipScope === 'incoming' && selectedTitipIds.length > 0 && titipanUntukku.length === selectedTitipIds.length}
                        onChange={() => toggleSelectAllTitip(titipanUntukku, 'incoming')}
                        style={{ cursor: 'pointer', width: 18, height: 18 }}
                      />
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
                        {selectedTitipScope !== 'incoming' || selectedTitipIds.length === 0 ? 'Pilih Semua' : `${selectedTitipIds.length} Terpilih`}
                      </span>
                    </div>
                    {selectedTitipScope === 'incoming' && selectedTitipIds.length > 0 && (
                      <button
                        onClick={() => selectedTitipIds.forEach(id => handleReceiveTitipan(id))}
                        style={{
                          padding: '8px 16px',
                          borderRadius: 8,
                          border: 'none',
                          backgroundColor: '#3b82f6',
                          color: '#fff',
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                        }}
                        onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.95)')}
                        onMouseUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                      >
                        📥 Terima ({selectedTitipIds.length})
                      </button>
                    )}
                  </div>

                  {(showAllUntukku ? titipanUntukku : titipanUntukku.slice(0, HO_LIMIT)).map(t => {
                    const isSelected = selectedTitipScope === 'incoming' && selectedTitipIds.includes(t.id);
                    return (
                      <div key={t.id} onClick={() => setSelectedTitipQR(t)} style={{ padding: '16px', borderRadius: 18, backgroundColor: isSelected ? '#dbeafe' : '#eff6ff', border: isSelected ? '2px solid #3b82f6' : '1px solid #bfdbfe', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', transition: 'all 0.2s ease' }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => { e.stopPropagation(); toggleSelectTitip(t.id, 'incoming'); }}
                          onClick={(e) => e.stopPropagation()}
                          style={{ cursor: 'pointer', width: 18, height: 18 }}
                        />
                        <div style={{ fontSize: 24 }}>{t.category === 'Barang' ? '📦' : '📄'}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>{t.item_name}</div>
                          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                            Posisi: <b>{t.current_holder_name || t.sender_name}</b>
                          </div>
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); handleReceiveTitipan(t.id); }} style={{ padding: '8px 12px', borderRadius: 10, border: 'none', backgroundColor: '#2563eb', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Terima</button>
                      </div>
                    );
                  })}
                  {!showAllUntukku && titipanUntukku.length > HO_LIMIT && (
                    <button
                      onClick={() => setShowAllUntukku(true)}
                      style={{ marginTop: 4, width: '100%', padding: '10px', borderRadius: 12, border: '1px dashed #cbd5e1', backgroundColor: '#f8fafc', color: '#2563eb', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s ease' }}
                    >
                      👁 Lihat Semua ({titipanUntukku.length - HO_LIMIT} item lainnya)
                    </button>
                  )}
                  {showAllUntukku && titipanUntukku.length > HO_LIMIT && (
                    <button
                      onClick={() => setShowAllUntukku(false)}
                      style={{ marginTop: 4, width: '100%', padding: '10px', borderRadius: 12, border: '1px dashed #cbd5e1', backgroundColor: '#f8fafc', color: '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s ease' }}
                    >
                      ↑ Sembunyikan
                    </button>
                  )}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8', backgroundColor: '#f8fafc', borderRadius: 20, border: '1px dashed #e2e8f0' }}>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>👥</div>
                  <p style={{ fontSize: 13, margin: 0 }}>Belum ada data serah terima masuk</p>
                </div>
              )}
            </motion.div>

            {/* Riwayat Diterima Untukku */}
            {titipanUntukkuSelesai.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} style={{ backgroundColor: '#fff', borderRadius: 24, padding: '24px 20px', width: '100%', maxWidth: 450, margin: '0 auto 24px auto', boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}>
                <div style={{ textAlign: 'center', marginBottom: 32, paddingBottom: 24, borderBottom: '1px solid #f0f0f0' }}>
                  <h2 style={{ fontWeight: 700, fontSize: 18, margin: '0 0 2px 0', color: '#111827' }}>Riwayat Serah Terima Selesai</h2>
                  <p style={{ margin: 0, color: '#9ca3af', fontSize: 13 }}>{titipanUntukkuSelesai.length} barang telah selesai diterima</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, fontSize: 13, color: '#475569', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={selectedCompletedTitipIds.length === titipanUntukkuSelesai.length}
                      onChange={() => toggleSelectAllCompletedTitip(titipanUntukkuSelesai)}
                    />
                    {selectedCompletedTitipIds.length > 0
                      ? `${selectedCompletedTitipIds.length} Terpilih`
                      : 'Pilih Semua'}
                  </label>
                  {selectedCompletedTitipIds.length > 0 && (
                    <button
                      onClick={handleDeleteCompletedTitip}
                      style={{ border: 'none', borderRadius: 10, padding: '9px 14px', backgroundColor: '#ef4444', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                    >
                      Hapus ({selectedCompletedTitipIds.length})
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {(showAllSelesai ? titipanUntukkuSelesai : titipanUntukkuSelesai.slice(0, HO_LIMIT)).map(t => {
                    const isSelected = selectedCompletedTitipIds.includes(t.id);
                    return (
                    <div key={t.id} onClick={() => setSelectedTitipQR(t)} style={{ padding: '16px', borderRadius: 18, backgroundColor: isSelected ? '#fef2f2' : '#f8fafc', border: isSelected ? '1px solid #ef4444' : '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onClick={(event) => event.stopPropagation()}
                        onChange={() => toggleSelectCompletedTitip(t.id)}
                        aria-label={`Pilih ${t.item_name}`}
                      />
                      <div style={{ fontSize: 24 }}>{t.category === 'Barang' ? '📦' : '📄'}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>{t.item_name}</div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                          Pengirim: <b>{t.sender_name}</b> | Diterima: {t.receive_time ? new Date(t.receive_time).toLocaleString('id-ID', { dateStyle: 'medium', timeStyle: 'short' }) : 'Selesai'}
                        </div>
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 700, padding: '4px 8px', borderRadius: 8, backgroundColor: '#dcfce7', color: '#166534' }}>
                        Selesai
                      </div>
                    </div>
                  )})}
                  {!showAllSelesai && titipanUntukkuSelesai.length > HO_LIMIT && (
                    <button
                      onClick={() => setShowAllSelesai(true)}
                      style={{ marginTop: 4, width: '100%', padding: '10px', borderRadius: 12, border: '1px dashed #cbd5e1', backgroundColor: '#f8fafc', color: '#2563eb', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s ease' }}
                    >
                      👁 Lihat Semua ({titipanUntukkuSelesai.length - HO_LIMIT} item lainnya)
                    </button>
                  )}
                  {showAllSelesai && titipanUntukkuSelesai.length > HO_LIMIT && (
                    <button
                      onClick={() => setShowAllSelesai(false)}
                      style={{ marginTop: 4, width: '100%', padding: '10px', borderRadius: 12, border: '1px dashed #cbd5e1', backgroundColor: '#f8fafc', color: '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s ease' }}
                    >
                      ↑ Sembunyikan
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </div>
        )}

        {/* ── Tab Asset ── */}
        {activeTab === 'asset' && (
          <AssetTab />
        )}

        {/* Modal OB */}
        <ObFormModal
          isOpen={isObModalOpen}
          onClose={() => {
            setIsObModalOpen(false);
            setSelectedObTask(null);
            setObProgressRefresh(prev => prev + 1);
            fetchServerReports();
            fetchExtraTasks();
          }}
          onImageSelected={(taskId, index) => {
            setObReportsFromServer(prev => [...prev, { task_id: taskId, subtask_index: index, user_id: dataKaryawan?.id }]);
            setOptimisticPendingReports(prev => [...prev, { task_id: taskId, subtask_index: index }]);
          }}
          task={selectedObTask}
          serverReports={obReportsFromServer}
          apiQueue={apiQueue}
        />

        <HandoverDeliveryModal
          open={deliveryItems.length > 0}
          itemCount={deliveryItems.length}
          defaultRecipientName={deliveryItems[0]?.recipient_name}
          onClose={() => setDeliveryItems([])}
          onSubmit={handleDeliveryMode}
        />

        {/* QR Modal */}
        <AnimatePresence>
          {selectedTitipQR && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
              <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} style={{ backgroundColor: '#fff', borderRadius: 24, padding: '32px 24px', maxWidth: 430, width: '100%', maxHeight: '90vh', overflowY: 'auto', textAlign: 'center', position: 'relative', boxShadow: '0 20px 40px rgba(0,0,0,0.3)' }}>
                <button onClick={() => setSelectedTitipQR(null)} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}><X size={24} /></button>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>
                  {String(selectedTitipQR.status).toLowerCase() === 'selesai' || (selectedTitipQR.handover_mode && selectedTitipQR.handover_mode !== HANDOVER_MODES.QR)
                    ? 'Detail Serah Terima'
                    : 'QR Serah Terima'}
                </h3>
                {String(selectedTitipQR.status).toLowerCase() !== 'selesai' && (!selectedTitipQR.handover_mode || selectedTitipQR.handover_mode === HANDOVER_MODES.QR) && (
                  <>
                    <p style={{ fontSize: 13, color: '#64748b', marginBottom: 24 }}>Tunjukkan ke Petugas/Penerima</p>
                    <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: 16, display: 'inline-block', marginBottom: 20 }}>
                      <QRCode value={selectedTitipQR.id} size={180} />
                    </div>
                  </>
                )}
                <div style={{ textAlign: 'left', backgroundColor: '#f1f5f9', padding: '16px', borderRadius: 16, marginBottom: 20 }}>
                  {selectedTitipQR.isBulk ? (
                    <>
                      <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #cbd5e1' }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>
                          📦 {selectedTitipQR.items?.length || 0} Barang Gabungan
                        </div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>
                          Tujuan Akhir: <b>{selectedTitipQR.recipient_name} ({selectedTitipQR.kantor})</b>
                        </div>
                      </div>
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 8 }}>Daftar Barang:</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 150, overflowY: 'auto' }}>
                          {selectedTitipQR.items?.slice(0, 5).map((item, idx) => (
                            <div key={item.id} style={{ fontSize: 11, padding: '6px 8px', backgroundColor: '#fff', borderRadius: 6, border: '1px solid #e2e8f0' }}>
                              <span style={{ color: '#475569' }}>
                                {idx + 1}. {item.item_name}
                                <span style={{ display: 'block', fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
                                  ID: {item.id}
                                </span>
                              </span>
                            </div>
                          ))}
                          {selectedTitipQR.items && selectedTitipQR.items.length > 5 && (
                            <div style={{ fontSize: 11, padding: '8px 10px', backgroundColor: '#eef2ff', borderRadius: 8, border: '1px solid #c7d2fe', color: '#4338ca' }}>
                              +{selectedTitipQR.items.length - 5} item lainnya
                            </div>
                          )}
                        </div>
                      </div>
                      <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #cbd5e1', fontSize: 11, color: '#64748b' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span>Kode Gabungan:</span>
                          <span style={{ fontFamily: 'monospace', fontSize: 10, color: '#1e293b' }}>
                            {selectedTitipQR.id?.substring(0, 20)}...
                          </span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {[
                        ['Item ID', selectedTitipQR.id],
                        ['Nama Item', selectedTitipQR.item_name],
                        ['Kategori', selectedTitipQR.category],
                        ['Tujuan Akhir', `${selectedTitipQR.recipient_name} (${selectedTitipQR.kantor})`],
                        ['Pemegang Sekarang', selectedTitipQR.current_holder_name || selectedTitipQR.sender_name],
                        ['Status Akhir', selectedTitipQR.status],
                        ['Metode', selectedTitipQR.handover_mode === 'manual' ? 'Input Nama' : selectedTitipQR.handover_mode === 'unattended' ? 'Foto + Keterangan' : 'Barcode'],
                      ].map(([label, value]) => (
                        <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, gap: 12 }}>
                          <span style={{ fontSize: 12, color: '#64748b', flexShrink: 0 }}>{label}:</span>
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', textAlign: 'right' }}>{value}</span>
                        </div>
                      ))}
                    </>
                  )}
                </div>

                {/* Timeline Alur Serah Terima */}
                <div style={{ marginTop: 20, textAlign: 'left', borderTop: '1px dashed #cbd5e1', paddingTop: 20 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#475569', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
                    ⏳ Alur & Timeline Serah Terima
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 20, position: 'relative', paddingLeft: 18, marginLeft: 8 }}>
                    <style>{`
                      @keyframes pulseBlue {
                        0% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.7), 0 0 0 2px rgba(0,0,0,0.08); }
                        70% { box-shadow: 0 0 0 8px rgba(37, 99, 235, 0), 0 0 0 2px rgba(0,0,0,0.08); }
                        100% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0), 0 0 0 2px rgba(0,0,0,0.08); }
                      }
                      @keyframes pulseGreen {
                        0% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7), 0 0 0 2px rgba(0,0,0,0.08); }
                        70% { box-shadow: 0 0 0 8px rgba(16, 185, 129, 0), 0 0 0 2px rgba(0,0,0,0.08); }
                        100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0), 0 0 0 2px rgba(0,0,0,0.08); }
                      }
                      @keyframes pulseOrange {
                        0% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.7), 0 0 0 2px rgba(0,0,0,0.08); }
                        70% { box-shadow: 0 0 0 8px rgba(245, 158, 11, 0), 0 0 0 2px rgba(0,0,0,0.08); }
                        100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0), 0 0 0 2px rgba(0,0,0,0.08); }
                      }
                    `}</style>

                    {(() => {
                      const rawTimeline = selectedTitipQR.timeline || [
                        {
                          status: 'Dibuat',
                          actor_name: selectedTitipQR.sender_name,
                          timestamp: selectedTitipQR.timestamp,
                          notes: `Handover dibuat oleh ${selectedTitipQR.sender_name}`
                        },
                        ...(selectedTitipQR.status === 'Selesai' || selectedTitipQR.status === 'Diterima' ? [{
                          status: selectedTitipQR.status,
                          actor_name: selectedTitipQR.receiver_name || selectedTitipQR.recipient_name,
                          timestamp: selectedTitipQR.receive_time || selectedTitipQR.timestamp,
                          notes: `Diterima oleh ${selectedTitipQR.receiver_name || selectedTitipQR.recipient_name}`
                        }] : [])
                      ];

                      // Saring duplikat berturut-turut
                      return rawTimeline.filter((event, index, self) => {
                        if (index === 0) return true;
                        const prev = self[index - 1];
                        return !(
                          event.status === prev.status &&
                          (event.actor_name === prev.actor_name || event.actor_id === prev.actor_id) &&
                          event.notes === prev.notes
                        );
                      });
                    })().map((event, idx, arr) => {
                      const isLast = idx === arr.length - 1;
                      const isComplete = event.status === 'Selesai' || event.status === 'Diterima';
                      const isWaiting = event.status === 'Menunggu Diterima';
                      const isTransit = event.status === 'Diserahkan' || isWaiting;

                      let dotColor = '#2563eb';
                      if (isComplete) dotColor = '#10b981';
                      else if (isTransit) dotColor = '#f59e0b';

                      let pulseAnimation = 'none';
                      if (isLast) {
                        pulseAnimation = isComplete
                          ? 'pulseGreen 1.8s infinite'
                          : isTransit
                            ? 'pulseOrange 1.8s infinite'
                            : 'pulseBlue 1.8s infinite';
                      }

                      return (
                        <div key={idx} style={{ position: 'relative' }}>
                          {/* Line segment between this item and the next */}
                          {!isLast && (
                            <div style={{
                              position: 'absolute',
                              left: -19.75, // mathematically aligns center of dot
                              top: 16,   // starts from bottom of current dot
                              bottom: -24, // goes all the way to next item
                              width: '2.5px',
                              backgroundColor: '#e2e8f0',
                              zIndex: 1
                            }} />
                          )}

                          {/* Dot */}
                          <div style={{
                            position: 'absolute',
                            left: -27.5,
                            top: 4,
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: dotColor,
                            border: '3px solid #fff',
                            boxShadow: '0 0 0 2px rgba(0,0,0,0.08)',
                            animation: pulseAnimation,
                            zIndex: 2
                          }} />

                          <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 6 }}>
                            {isComplete ? '✅ Selesai Diterima' : isTransit ? '📦 Transit' : '🆕 Dibuat'}
                          </div>

                          <div style={{ fontSize: 12, color: '#475569', marginTop: 3 }}>
                            {(event.notes || `Oleh: ${event.actor_name}`).replace(' Sementara', '')}
                          </div>

                          {event.method && (
                            <div style={{ display: 'inline-block', marginTop: 5, padding: '3px 7px', borderRadius: 7, background: '#e0e7ff', color: '#4338ca', fontSize: 10, fontWeight: 700 }}>
                              {event.method === 'manual' ? 'Input Nama' : event.method === 'unattended' ? 'Foto + Keterangan' : 'Barcode'}
                            </div>
                          )}

                          {event.photo && (
                            <img src={event.photo} alt="Bukti penyerahan" style={{ display: 'block', width: '100%', maxHeight: 190, objectFit: 'cover', borderRadius: 12, marginTop: 8 }} />
                          )}

                          <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2, fontFamily: 'monospace' }}>
                            {new Date(event.timestamp).toLocaleString('id-ID', { dateStyle: 'medium', timeStyle: 'short' })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default Lapor;
